import { useEffect, useState, type ReactNode } from "react";
import { AdminLayout } from "../../components/AdminLayout";
import { ConfirmDialog } from "../../components/ConfirmDialog";
import { adminHeaders, api } from "../../lib/api";
import { money, shortDate } from "../../lib/format";
import {
  actionLabel,
  copyRawJson,
  formatAdminLogSummary,
  formatGlory,
  formatReviewRequestSummary,
  getGuildLevelInfo,
  prettyRawJson,
  reviewStatusLabel,
  reviewTypeLabel,
  rewardStatusLabel,
  rewardTypeLabel
} from "../../lib/adminDisplayLabels";
import { isLocalDevFrontend } from "../../lib/env";

interface MemberRow { id: number; member_account: string; identity_code: string; status: string; status_label: string; bound_at: string; is_test: number; activation_deposit_id?: number | null; channel_confirmed_at?: string | null; graduation_at?: string | null; }
interface DepositRow { id: number; member_account: string; amount: number; deposited_at: string; note?: string | null; is_test: number; }
interface RewardRow {
  id: number;
  reward_type: string;
  reward_status: string;
  amount: number;
  created_at: string;
  claimed_at?: string | null;
  source_deposit_id?: number | null;
  unique_key?: string | null;
  invite_manual_review_id?: number | null;
  source_inviter_account?: string | null;
  source_invitee_account?: string | null;
  source_invitee_line_nickname?: string | null;
  crawler_dispatch_task_id?: number | null;
  crawler_dispatch_mode?: string | null;
  crawler_dispatch_status?: string | null;
  crawler_dispatch_scheduled_at?: string | null;
  crawler_dispatch_queued_at?: string | null;
  crawler_dispatch_started_at?: string | null;
  crawler_dispatch_completed_at?: string | null;
  crawler_dispatch_failed_at?: string | null;
  crawler_dispatch_failed_reason?: string | null;
  crawler_dispatch_attempt_count?: number | null;
  crawler_dispatch_last_attempt_at?: string | null;
  crawler_reference_id?: string | null;
}
interface AdminLogRow { id: number; action: string; target_type: string; target_id?: string | null; payload?: string | null; created_at: string; }
interface AssetLedgerRow { id: number; asset_type: string; amount: number; balance_after?: number | null; activity_code: string; reason_code: string; unique_key: string; source_record_id?: string | null; note?: string | null; created_at: string; }
interface VoucherSummaryRow { voucher_type: string; status: string; count: number; }
interface VoucherTransactionRow { id: number; voucher_id?: number | null; voucher_type: string; action: string; activity_code: string; unique_key: string; note?: string | null; created_at: string; }
interface NewbieRedPacketDrawRow { id: number; draw_ref: string; voucher_id?: number | null; voucher_type: string; prize_amount: number; prize_name?: string | null; reward_id?: number | null; reward_type?: string | null; reward_status?: string | null; status: string; created_at: string; updated_at?: string | null; }
interface InviteRelationRow { id: number; invitee_account: string; phase: string; status: string; created_at: string; confirmed_at?: string | null; reward_id?: number | null; reward_amount?: number | null; dispatch_status?: string | null; source_deposit_id?: number | null; }
interface InviteManualReviewRow {
  id: number;
  inviter_account: string;
  invitee_account: string;
  line_nickname?: string | null;
  line_added: number;
  group_joined: number;
  own_channel: number;
  first_deposit_amount?: number | null;
  second_deposit_amount?: number | null;
  third_deposit_amount?: number | null;
  join_reward_id?: number | null;
  first_deposit_reward_id?: number | null;
  second_stage_reward_id?: number | null;
  third_deposit_reward_id?: number | null;
  join_reward_status?: string | null;
  first_deposit_reward_status?: string | null;
  second_stage_reward_status?: string | null;
  third_deposit_reward_status?: string | null;
  updated_at: string;
}
interface InviteCodeInfo { invite_code?: string | null; invite_link?: string | null; }
interface ReviewRequestRow { id: number; request_no?: string | null; member_account: string; review_type: string; status: string; title?: string | null; amount?: number | null; reward_type?: string | null; risk_note?: string | null; applicant_note?: string | null; payload_json?: string | null; review_note?: string | null; reject_reason?: string | null; cancel_reason?: string | null; reviewed_at?: string | null; created_at: string; unique_key?: string | null; }
interface NewbieRescueAttemptRow {
  id: number;
  failure_record_id: number;
  member_id?: number | null;
  member_account: string;
  step_code: string;
  rescue_no: number;
  rescue_action: string;
  rescue_reason: string;
  related_review_id?: number | null;
  related_reward_id?: number | null;
  before_json?: string | null;
  after_json?: string | null;
  admin_note?: string | null;
  created_by?: string | null;
  created_at: string;
  unique_key?: string | null;
}
interface NewbieFailureRecordRow {
  id: number;
  member_id?: number | null;
  member_account: string;
  step_code: string;
  failure_type: string;
  failure_reason: string;
  failed_at: string;
  rescue_window_start_at: string;
  rescue_window_end_at: string;
  rescue_count: number;
  max_rescue_count: number;
  status: string;
  computed_status?: string;
  status_label?: string;
  can_rescue?: boolean;
  remaining_rescue_count?: number;
  is_expired?: boolean;
  is_exhausted?: boolean;
  window_end?: string;
  next_step_message?: string;
  related_review_id?: number | null;
  related_reward_id?: number | null;
  related_deposit_id?: number | null;
  admin_note?: string | null;
  created_by?: string | null;
  created_at: string;
  updated_at?: string | null;
  attempts?: NewbieRescueAttemptRow[];
}
interface RegularRebateCandidateRow { deposit_id: number; deposit_amount: number; deposited_at: string; rebate_percent: number; reward_amount: number; status: "eligible" | "created" | "unavailable" | "manual_review"; status_label: string; unavailable_reasons: string[]; manual_confirm_reasons: string[]; existing_reward_id?: number | null; existing_reward_status?: string | null; }
interface WeekendBonusProgressRow { id: number; weekend_id: string; weekend_start_at: string; weekend_end_at: string; checkin_completed_at?: string | null; single_1000_completed_at?: string | null; single_1000_deposit_id?: number | null; single_1000_voucher_id?: number | null; cumulative_5000_completed_at?: string | null; cumulative_5000_amount?: number | null; cumulative_5000_deposit_ids_json?: string | null; cumulative_5000_voucher_id?: number | null; created_at: string; updated_at: string; }
type VipNewbieSpecialMode = "activation_only" | "activation_second" | "activation_second_third";
interface VipNewbieSpecialPayload { mode: VipNewbieSpecialMode; activation_deposit_id: number; second_deposit_id?: number | null; third_deposit_id?: number | null; reason: string; confirm_ready_only: boolean; }
interface VipNewbieSpecialRewardResult { id: number; reward_type: string; amount: number; reward_status: string; source_deposit_id?: number | null; created?: boolean; }
interface VipNewbieSpecialResult { rewards: VipNewbieSpecialRewardResult[]; created_rewards?: VipNewbieSpecialRewardResult[]; existing_rewards?: VipNewbieSpecialRewardResult[]; created_reward_ids?: number[]; existing_reward_ids?: number[]; message?: string; }
interface NewbieProgressRow {
  activation_deposit_id?: number | null;
  step1_status?: string | null; step1_reward_id?: number | null; step1_completed_at?: string | null;
  step2_status?: string | null; step2_reward_id?: number | null; step2_completed_at?: string | null;
  step3_status?: string | null; step3_second_deposit_id?: number | null; step3_deposit_id?: number | null; step3_reward_id?: number | null; step3_completed_at?: string | null; step3_gem_granted_at?: string | null;
  step4_status?: string | null; step4_reward_id?: number | null; step4_completed_at?: string | null;
  step5_status?: string | null; step5_deposit_id?: number | null; step5_reward_id?: number | null; step5_completed_at?: string | null; step5_gem_granted_at?: string | null;
  step6_status?: string | null; step6_voucher_id?: number | null; step6_draw_id?: string | null; step6_draw_record_id?: number | null; step6_draw_ref?: string | null; step6_reward_id?: number | null; step6_completed_at?: string | null; step6_gem_granted_at?: string | null;
  step7_available_at?: string | null; step7_status?: string | null; step7_unlocked_at?: string | null; step7_final10_reward_id?: number | null; step7_graduation_reward_id?: number | null; step7_glory_granted_at?: string | null; step7_modules_unlocked_at?: string | null; step7_completed_at?: string | null;
  invite_unlocked_at?: string | null;
}
interface MemberDetail {
  member: MemberRow; deposits: DepositRow[]; rewards: RewardRow[]; admin_logs: AdminLogRow[];
  activation_deposit?: { amount?: number | string } | null;
  assets?: { gems: number; vouchers?: VoucherSummaryRow[]; glory_value?: number };
  asset_ledgers?: AssetLedgerRow[]; voucher_transactions?: VoucherTransactionRow[]; newbie_red_packet_draws?: NewbieRedPacketDrawRow[];
  invite_relations?: InviteRelationRow[]; invite_manual_reviews?: InviteManualReviewRow[]; invite_status?: { is_unlocked: boolean; unlocked_at?: string | null }; invite_code?: InviteCodeInfo | null;
  newbie_progress?: NewbieProgressRow | null;
  step1_reward?: RewardRow | null; step2_reward?: RewardRow | null; step3_reward?: RewardRow | null; step4_reward?: RewardRow | null; step5_reward?: RewardRow | null; step6_reward?: RewardRow | null; step7_final10_reward?: RewardRow | null; step7_graduation_reward?: RewardRow | null;
  review_requests?: ReviewRequestRow[];
  regular_rebate_candidates?: RegularRebateCandidateRow[];
  weekend_bonus_progress?: WeekendBonusProgressRow[];
}

interface PendingMemberOperation {
  key: string;
  title: string;
  message: string;
  confirmText: string;
  success: string;
  variant?: "normal" | "danger";
  action: () => Promise<unknown>;
}

type MemberDetailTab = "newbie" | "graduated" | "rewards" | "assets" | "reviews";
type DecisionTarget = MemberDetailTab;
type DecisionTone = "success" | "good" | "soon" | "danger" | "muted" | "locked";

function readableAdminError(err: unknown, fallback: string) {
  const message = err instanceof Error ? err.message : "";
  if (!message || message.includes("undefined") || message.includes("null") || message.toLowerCase().includes("stack")) {
    return fallback;
  }
  return message;
}

function memberActionKey(action: string, id?: number | string) {
  return id === undefined ? action : `${action}:${id}`;
}

export function AdminMemberDetailPage() {
  const account = decodeURIComponent(window.location.pathname.replace("/admin/members/", ""));
  const isLocalDev = isLocalDevFrontend();
  const [detail, setDetail] = useState<MemberDetail | null>(null);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [pendingOperation, setPendingOperation] = useState<PendingMemberOperation | null>(null);
  const [activeOperationKey, setActiveOperationKey] = useState("");
  const [activeTab, setActiveTab] = useState<MemberDetailTab>("newbie");

  async function load() {
    setError("");
    const nextDetail = await api<MemberDetail>(`/api/admin/members/${encodeURIComponent(account)}`, { headers: adminHeaders() });
    setDetail(nextDetail);
  }

  async function runAction(action: () => Promise<unknown>, success: string, key = "member-detail-action") {
    setMessage("");
    setError("");
    setActiveOperationKey(key);
    try {
      await action();
      setMessage(success);
      await load();
      return true;
    } catch (err) {
      setError(readableAdminError(err, "操作失敗，請稍後再試。"));
      return false;
    } finally {
      setActiveOperationKey("");
    }
  }

  async function createVipNewbieSpecial(payload: VipNewbieSpecialPayload) {
    setMessage("");
    setError("");
    setActiveOperationKey("vip-newbie-special");
    try {
      const result = await api<VipNewbieSpecialResult>(`/api/admin/members/${encodeURIComponent(account)}/create-vip-newbie-special`, {
        method: "POST",
        headers: adminHeaders(),
        body: JSON.stringify(payload)
      });
      setMessage(result.message ?? "已建立大客新人特批派發紀錄。");
      await load();
      return result;
    } catch (err) {
      setError(readableAdminError(err, "建立大客新人特批失敗，請稍後再試。"));
      throw err;
    } finally {
      setActiveOperationKey("");
    }
  }

  function requestOperation(operation: PendingMemberOperation) {
    setMessage("");
    setError("");
    setPendingOperation(operation);
  }

  async function confirmPendingOperation() {
    if (!pendingOperation) return;
    const completed = await runAction(pendingOperation.action, pendingOperation.success, pendingOperation.key);
    if (completed) setPendingOperation(null);
  }

  function navigateToTab(tab: MemberDetailTab) {
    setActiveTab(tab);
    window.setTimeout(() => {
      document.getElementById("member-stage-content")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 0);
  }

  function navigateToDecisionTarget(target: DecisionTarget) {
    navigateToTab(target);
  }

  useEffect(() => { load().catch((err) => setError(err instanceof Error ? err.message : "讀取會員失敗")); }, [account]);

  return <AdminLayout title={`會員詳情：${account}`}>
    <section className="admin-card">
      <a className="admin-table-link" href="/admin/members">返回會員列表</a>
      {message ? <p className="result">{message}</p> : null}
      {error ? <p className="error-box">{error}</p> : null}
    </section>
    {detail ? <>
      <MemberOverview detail={detail} />
      <CustomerDecisionOverview detail={detail} onNavigate={navigateToDecisionTarget} />
      <MemberStatusActions
        detail={detail}
        activeOperationKey={activeOperationKey}
        onConfirmChannel={() => requestOperation({
          key: "confirm-channel",
          title: "確認更新會員渠道？",
          message: "系統會將此會員標記為渠道已確認。請確認會員資料與渠道來源無誤後再繼續。",
          confirmText: "確認更新",
          success: "已更新會員渠道狀態。",
          action: () => api(`/api/admin/members/${encodeURIComponent(account)}/confirm-channel`, { method: "POST", headers: adminHeaders(), body: JSON.stringify({ note: "member detail status action" }) })
        })}
      />
      <MemberDetailTabs activeTab={activeTab} onChange={setActiveTab} detail={detail} />
      <div id="member-stage-content">
      {activeTab === "newbie" ? <>
        <NewbieQuestRecord detail={detail} />
        <DepositsSection
          detail={detail}
          activeOperationKey={activeOperationKey}
          onSelectActivationDeposit={(depositId) => requestOperation({
            key: memberActionKey("select-activation", depositId),
            title: "確認選定入盟啟動筆？",
            message: `系統會將存款 #${depositId} 設為此會員入盟啟動筆，並影響後續新人任務判斷。請確認資料無誤後再繼續。`,
            confirmText: "確認選定",
            success: "已選定入盟啟動筆。",
            action: () => api(`/api/admin/members/${encodeURIComponent(account)}/select-activation-deposit`, { method: "POST", headers: adminHeaders(), body: JSON.stringify({ deposit_id: depositId }) })
          })}
          onSelectStep3Deposit={(depositId) => requestOperation({
            key: memberActionKey("select-step3", depositId),
            title: "確認建立第 3 關二存加碼？",
            message: `系統會使用存款 #${depositId} 檢查並建立第 3 關二存加碼相關紀錄。請確認這筆存款正確後再繼續。`,
            confirmText: "確認建立",
            success: "已選定第 3 關二存加碼筆。",
            action: () => api(`/api/admin/members/${encodeURIComponent(account)}/check-step3-second-deposit`, { method: "POST", headers: adminHeaders(), body: JSON.stringify({ deposit_id: depositId }) })
          })}
          onSelectStep5Deposit={(depositId) => requestOperation({
            key: memberActionKey("select-step5", depositId),
            title: "確認建立第 5 關三存加碼？",
            message: `系統會使用存款 #${depositId} 檢查並建立第 5 關三存加碼相關紀錄。請確認這筆存款正確後再繼續。`,
            confirmText: "確認建立",
            success: "已選定第 5 關三存進階筆。",
            action: () => api(`/api/admin/members/${encodeURIComponent(account)}/check-step5-third-deposit`, { method: "POST", headers: adminHeaders(), body: JSON.stringify({ deposit_id: depositId }) })
          })}
        />
        <VipNewbieSpecialSection detail={detail} onCreate={createVipNewbieSpecial} />
      </> : null}
      {activeTab === "graduated" ? <>
        <PostGraduationOverviewSection detail={detail} />
        <RegularRebateSectionV2
          detail={detail}
          activeOperationKey={activeOperationKey}
          onCreate={(depositId, manualConfirm) => requestOperation({
            key: memberActionKey(manualConfirm?.manual_confirm ? "regular-rebate-manual" : "regular-rebate", depositId),
            title: manualConfirm?.manual_confirm ? "確認人工建立常態回饋？" : "確認建立常態回饋派發？",
            message: manualConfirm?.manual_confirm
              ? `系統會依人工確認結果，為存款 #${depositId} 建立正式成員常態回饋派發紀錄。請確認備註與資格無誤後再繼續。`
              : `系統會依目前規則，為存款 #${depositId} 建立正式成員常態回饋派發紀錄。請確認資料無誤後再繼續。`,
            confirmText: "確認建立",
            success: "已建立正式成員常態回饋派發。",
            action: () => api(`/api/admin/members/${encodeURIComponent(account)}/create-regular-rebate`, { method: "POST", headers: adminHeaders(), body: JSON.stringify({ deposit_id: depositId, reason: "member detail regular rebate", ...manualConfirm }) })
          })}
        />
        <WeekendBonusSection rows={detail.weekend_bonus_progress ?? []} />
      </> : null}
      {activeTab === "rewards" ? <>
        <RewardSummarySection rows={detail.rewards} />
        <InviteSection rows={detail.invite_relations ?? []} unlocked={detail.invite_status?.is_unlocked ?? false} unlockedAt={detail.invite_status?.unlocked_at} inviteCode={detail.invite_code} />
        <InviteManualReviewSection rows={detail.invite_manual_reviews ?? []} account={detail.member.member_account} />
      </> : null}
      {activeTab === "assets" ? <>
        <AssetSummarySection detail={detail} />
        <GuildLevelSection detail={detail} />
      </> : null}
      {activeTab === "reviews" ? <>
        <ReviewsSection rows={detail.review_requests ?? []} />
        <LogsSection rows={detail.admin_logs} />
      </> : null}
      </div>
      {isLocalDev ? <LocalDevTools account={account} activeOperationKey={activeOperationKey} requestOperation={requestOperation} /> : null}
    </> : null}
    <ConfirmDialog
      open={Boolean(pendingOperation)}
      title={pendingOperation?.title ?? ""}
      message={pendingOperation?.message ?? ""}
      confirmText={pendingOperation?.confirmText ?? "確認執行"}
      cancelText="取消"
      variant={pendingOperation?.variant ?? "normal"}
      loading={Boolean(pendingOperation && activeOperationKey === pendingOperation.key)}
      onCancel={() => {
        if (!activeOperationKey) setPendingOperation(null);
      }}
      onConfirm={confirmPendingOperation}
    />
  </AdminLayout>;
}

function MemberOverview({ detail }: { detail: MemberDetail }) {
  const vouchers = detail.assets?.vouchers ?? [];
  return <section className="admin-card"><div className="admin-card-head"><span>MEMBER OVERVIEW</span><h2>會員總覽</h2></div><div className="admin-overview-grid">
    <InfoCard label="會員帳號" value={detail.member.member_account} note={`手機後 3 碼：${detail.member.identity_code}`} />
    <InfoCard label="會員狀態" value={detail.member.status_label} note={memberIsGraduated(detail) ? "正式成員" : "依新人階段進度判斷"} />
    <InfoCard label="新人進度" value={getMainProgress(detail)} note={`畢業時間：${shortDate(detail.member.graduation_at)}`} />
    <InfoCard label="入盟啟動筆" value={detail.activation_deposit?.amount ? money(detail.activation_deposit.amount) : detail.member.activation_deposit_id ?? "尚未指定"} note={detail.member.activation_deposit_id ? `存款 ID：${detail.member.activation_deposit_id}` : "等待後台選定"} />
    <InfoCard label="閃耀值" value={formatGlory(detail.assets?.glory_value ?? 0)} note="會員身份成長值" />
    <InfoCard label="寶石碎片" value={`${detail.assets?.gems ?? 0}`} note="第 3 / 5 / 6 關與活動取得" />
    <InfoCard label="紅包券摘要" value={voucherTotal(vouchers)} note={voucherSummary(vouchers)} />
    <InfoCard label="渠道狀態" value={detail.member.channel_confirmed_at ? "已確認" : "尚未確認"} note={shortDate(detail.member.channel_confirmed_at)} />
  </div></section>;
}

function InfoCard({ label, value, note }: { label: string; value: ReactNode; note?: ReactNode }) {
  return <article className="admin-overview-card"><span>{label}</span><strong>{value}</strong>{note ? <small>{note}</small> : null}</article>;
}

function DetailItem({ label, value }: { label: string; value: ReactNode }) {
  return <div className="member-detail-kv"><span>{label}</span><strong>{displayNode(value)}</strong></div>;
}

function CustomerDecisionOverview({ detail, onNavigate }: { detail: MemberDetail; onNavigate: (target: DecisionTarget) => void }) {
  const summary = buildCustomerDecisionSummary(detail);
  return <section className="admin-card customer-decision-card">
    <div className="admin-card-head"><span>CS SNAPSHOT</span><h2>客服判斷總覽</h2></div>
    <div className="customer-decision-grid">
      <DecisionCard title="目前階段" value={summary.stage} badge={summary.stageBadge} tone={summary.stageTone} detail={summary.stageDetail} actionLabel={summary.stageActionText} onAction={() => onNavigate(summary.stageTarget)} />
      <DecisionCard title="目前卡點" value={summary.blocker} badge={summary.blockerBadge} tone={summary.blockerTone} detail={summary.blockerDetail} actionLabel={summary.blockerActionText} onAction={() => onNavigate(summary.blockerTarget)} />
      <DecisionCard title="下一步建議" value={summary.nextStep} badge="下一步" tone={summary.blockerTone === "danger" ? "danger" : "soon"} detail={summary.nextStepDetail} actionLabel={summary.nextActionText} onAction={() => onNavigate(summary.nextTarget)} />
      <DecisionCard title="派發異常" value={summary.dispatchIssue} badge={summary.dispatchBadge} tone={summary.dispatchTone} detail={summary.dispatchDetail} actionLabel="前往派發紀錄" onAction={() => onNavigate("rewards")} />
      <DecisionCard title="可通知會員" value={summary.notifyText} badge={summary.notifyBadge} tone={summary.notifyTone} detail={summary.notifyDetail} actionLabel="前往派發紀錄" onAction={() => onNavigate("rewards")} />
      <DecisionCard title="紅包券 / 資產" value={summary.assetText} badge={summary.assetBadge} tone={summary.assetTone} detail={summary.assetDetail} actionLabel="前往資產 / 券" onAction={() => onNavigate("assets")} />
      <DecisionCard title="審核 / 人工處理" value={summary.reviewText} badge={summary.reviewBadge} tone={summary.reviewTone} detail={summary.reviewDetail} actionLabel="前往審核紀錄" onAction={() => onNavigate("reviews")} />
    </div>
  </section>;
}

function DecisionCard({ title, value, badge, tone, detail, actionLabel, onAction }: { title: string; value: string; badge: string; tone: DecisionTone; detail: string; actionLabel?: string; onAction?: () => void }) {
  return <article className="customer-decision-item">
    <div className="customer-decision-head">
      <span>{title}</span>
      <em className={`status-pill ${tone}`}>{badge}</em>
    </div>
    <strong>{value}</strong>
    <small>{detail}</small>
    {actionLabel && onAction ? <button type="button" className="customer-decision-action" onClick={onAction}>{actionLabel}</button> : null}
  </article>;
}

function MemberDetailTabs({ activeTab, onChange, detail }: { activeTab: MemberDetailTab; onChange: (tab: MemberDetailTab) => void; detail: MemberDetail }) {
  const tabs: Array<{ key: MemberDetailTab; label: string; note: string }> = [
    { key: "newbie", label: "新人階段", note: getMainProgress(detail) },
    { key: "graduated", label: "畢業後階段", note: postGraduationTabNote(detail) },
    { key: "rewards", label: "派發紀錄", note: `${detail.rewards.length} 筆` },
    { key: "assets", label: "資產 / 券", note: `${voucherTotal(detail.assets?.vouchers)} 張券` },
    { key: "reviews", label: "審核紀錄", note: `${detail.review_requests?.length ?? 0} 筆` }
  ];
  return <section className="admin-card member-stage-tabs-card">
    <div className="admin-card-head"><span>MEMBER STAGE</span><h2>會員階段切換</h2></div>
    <div className="member-stage-tabs" role="tablist" aria-label="會員詳情階段">
      {tabs.map((tab) => <button
        key={tab.key}
        type="button"
        className={activeTab === tab.key ? "member-stage-tab active" : "member-stage-tab"}
        onClick={() => onChange(tab.key)}
        role="tab"
        aria-selected={activeTab === tab.key}
      >
        <strong>{tab.label}</strong>
        <span>{tab.note}</span>
      </button>)}
    </div>
  </section>;
}

function PostGraduationOverviewSection({ detail }: { detail: MemberDetail }) {
  const isGraduated = memberIsGraduated(detail);
  const latestWeekend = latestWeekendProgress(detail.weekend_bonus_progress ?? []);
  const regularCandidate = latestRegularRebateCandidate(detail.regular_rebate_candidates ?? []);
  const regularReward = regularCandidate?.existing_reward_id ? detail.rewards.find((row) => row.id === regularCandidate.existing_reward_id) ?? null : null;
  const graduateRewards = postGraduationRewards(detail, ["graduate_launch_red_packet_draw", "advanced_launch_red_packet_draw"]);
  const weeklyRewards = postGraduationRewards(detail, ["weekly_bright_red_packet_draw"]);
  const weekendRewards = postGraduationRewards(detail, ["weekend_bright_red_packet_draw", "weekend_advanced_red_packet_draw"]);
  const payoutFocus = postGraduationPayoutFocus(detail);
  const failedOrStuck = payoutFocus.filter((row) => row.crawler_dispatch_status === "failed" || isGiftHelperProcessingTimedOut(row));
  const notifiable = payoutFocus.filter((row) => row.reward_status === "completed" && row.crawler_dispatch_status === "completed");
  const decisionSummary = buildCustomerDecisionSummary(detail);
  const vouchers = detail.assets?.vouchers ?? [];
  const graduateVoucherText = voucherBreakdownMany(vouchers, ["graduate_launch_red_packet", "advanced_launch_red_packet"]);
  const weeklyVoucherText = voucherBreakdownMany(vouchers, ["weekly_bright_red_packet"]);
  const weekendVoucherText = voucherBreakdownMany(vouchers, ["weekend_bright_red_packet", "weekend_advanced_red_packet"]);

  return <section className="admin-card"><div className="admin-card-head"><span>POST GRADUATION</span><h2>畢業後階段總覽</h2></div>
    <div className="summary-list">
      <p>{postGraduationNextStep(detail, failedOrStuck.length, notifiable.length)}</p>
    </div>
    <div className="member-post-grid">
      <PostGraduationCard title="畢業狀態" badge={isGraduated ? "已畢業" : "尚未畢業"} tone={isGraduated ? "good" : "muted"} nextStep={isGraduated ? "會員已進入畢業後階段，可查看常態回饋、週末任務與派發狀態。" : "尚未畢業，請先查看新人階段進度。"}>
        <div className="member-detail-kv-grid">
          <DetailItem label="畢業時間" value={shortDate(detail.member.graduation_at)} />
          <DetailItem label="目前會員狀態" value={detail.member.status_label} />
          <DetailItem label="新人流程" value={isGraduated ? "已完成新人階段" : getMainProgress(detail)} />
          <DetailItem label="可通知獎勵" value={notifiable.length ? `${notifiable.length} 筆` : "-"} />
        </div>
      </PostGraduationCard>

      <PostGraduationCard title="畢業後首週" badge={graduateWeekBadge(graduateVoucherText, graduateRewards)} tone={graduateRewards.length ? "good" : "muted"} nextStep={graduateWeekNextStep(graduateVoucherText, graduateRewards)}>
        <div className="member-detail-kv-grid">
          <DetailItem label="首週資料" value={graduateWeekHasData(graduateVoucherText, graduateRewards, detail) ? "已有資料" : "目前沒有畢業首週資料"} />
          <DetailItem label="紅包券" value={graduateVoucherText || "-"} />
          <DetailItem label="抽中河幣派發" value={rewardListSummary(graduateRewards)} />
          <DetailItem label="贈禮任務" value={rewardGiftHelperSummary(graduateRewards)} />
        </div>
      </PostGraduationCard>

      <PostGraduationCard title="週通行證" badge={weeklyVoucherText || weeklyRewards.length ? "有紀錄" : "無紀錄"} tone={weeklyRewards.length ? "good" : "muted"} nextStep={weeklyVoucherText || weeklyRewards.length ? "目前可從紅包券與開券派發紀錄判斷週通行證結果。" : "目前沒有週通行證紀錄。"}>
        <div className="member-detail-kv-grid">
          <DetailItem label="本週進度" value={weeklyVoucherText || weeklyRewards.length ? "依紅包券 / 派發紀錄判斷" : "目前沒有週通行證紀錄"} />
          <DetailItem label="週耀紅包券" value={weeklyVoucherText || "-"} />
          <DetailItem label="抽中河幣派發" value={rewardListSummary(weeklyRewards)} />
          <DetailItem label="贈禮任務" value={rewardGiftHelperSummary(weeklyRewards)} />
        </div>
      </PostGraduationCard>

      <PostGraduationCard title="週末任務 / 週末券" badge={latestWeekend ? "有週末紀錄" : "無紀錄"} tone={weekendRewards.length ? "good" : latestWeekend ? "soon" : "muted"} nextStep={latestWeekend ? "請依週末任務狀態與紅包券派發紀錄追蹤。" : "目前沒有週末任務紀錄。"}>
        <div className="member-detail-kv-grid">
          <DetailItem label="週末區間" value={latestWeekend ? `${shortDate(latestWeekend.weekend_start_at)} 至 ${shortDate(latestWeekend.weekend_end_at)}` : "目前沒有週末任務紀錄"} />
          <DetailItem label="打卡狀態" value={latestWeekend?.checkin_completed_at ? "已打卡" : "-"} />
          <DetailItem label="單筆滿 1,000" value={latestWeekend?.single_1000_completed_at ? `已完成 / 存款 #${latestWeekend.single_1000_deposit_id ?? "-"}` : "-"} />
          <DetailItem label="累積滿 5,000" value={latestWeekend?.cumulative_5000_completed_at ? `已完成 / ${money(latestWeekend.cumulative_5000_amount ?? 0)}` : "-"} />
          <DetailItem label="週末紅包券" value={weekendVoucherText || "-"} />
          <DetailItem label="抽中河幣派發" value={rewardListSummary(weekendRewards)} />
        </div>
      </PostGraduationCard>

      <PostGraduationCard title="常態回饋" badge={regularCandidate ? regularRebateStatusText(regularCandidate) : isGraduated ? "尚無可顯示存款" : "尚未開放"} tone={regularReward ? "good" : regularCandidate?.status === "eligible" ? "soon" : "muted"} nextStep={regularRebateNextStep(detail, regularCandidate, regularReward)}>
        <div className="member-detail-kv-grid">
          <DetailItem label="最近存款" value={regularCandidate ? `#${regularCandidate.deposit_id} / ${money(regularCandidate.deposit_amount)} / ${shortDate(regularCandidate.deposited_at)}` : "-"} />
          <DetailItem label="回饋比例" value={regularCandidate ? formatPercent(regularCandidate.rebate_percent) : "-"} />
          <DetailItem label="預計派發" value={regularCandidate ? money(regularCandidate.reward_amount) : "-"} />
          <DetailItem label="派發紀錄" value={regularReward ? `#${regularReward.id} / ${rewardStatusLabel(regularReward.reward_status)}` : regularCandidate?.existing_reward_id ? `#${regularCandidate.existing_reward_id}` : "-"} />
          <DetailItem label="贈禮任務" value={giftHelperSummary(regularReward)} />
        </div>
      </PostGraduationCard>

      <PostGraduationCard title="派發異常" badge={decisionSummary.dispatchBadge} tone={decisionSummary.dispatchTone === "success" ? "good" : decisionSummary.dispatchTone} nextStep={decisionSummary.dispatchDetail}>
        <div className="member-detail-kv-grid">
          <DetailItem label="派發失敗" value={`${decisionSummary.failedCount} 筆`} />
          <DetailItem label="處理逾時" value={`${decisionSummary.timeoutCount} 筆`} />
          <DetailItem label="最大重試" value={`${decisionSummary.maxAttemptCount} 筆`} />
          <DetailItem label="目前判斷" value={decisionSummary.dispatchIssue} />
        </div>
      </PostGraduationCard>

      <PostGraduationCard title="可通知會員" badge={decisionSummary.notifyBadge} tone={decisionSummary.notifyTone === "success" ? "good" : decisionSummary.notifyTone} nextStep={decisionSummary.notifyDetail}>
        <div className="member-detail-kv-grid">
          <DetailItem label="可通知候選" value={`${decisionSummary.notifiableCount} 筆`} />
          <DetailItem label="通知紀錄" value="資料尚未串接" />
          <DetailItem label="最近可通知" value={notifiable[0] ? `${rewardTypeLabel(notifiable[0].reward_type)} / 派發 #${notifiable[0].id}` : "-"} />
          <DetailItem label="操作位置" value="贈禮小幫手可通知會員清單" />
        </div>
      </PostGraduationCard>
    </div>

    <div className="member-post-payout-card">
      <div className="member-reward-card-head">
        <div><span>POST-GRADUATION PAYOUTS</span><h3>畢業後派發重點</h3></div>
        <strong>{payoutFocus.length} 筆</strong>
      </div>
      <p className={failedOrStuck.length ? "member-next-step danger" : "member-next-step"}>{postGraduationPayoutHint(payoutFocus, failedOrStuck.length, notifiable.length)}</p>
      <div className="member-reward-list">
        {payoutFocus.slice(0, 8).map((row) => <article className="member-reward-mini-card" key={row.id}>
          <div><strong>{rewardTypeLabel(row.reward_type)}</strong><span>派發 #{row.id}</span></div>
          <div className="member-reward-mini-meta">
            <span>{money(row.amount)}</span>
            <span>{rewardStatusLabel(row.reward_status)}</span>
            <span>{giftHelperSummary(row)}</span>
            <span>{row.reward_status === "completed" && row.crawler_dispatch_status === "completed" ? "可通知會員" : "尚不可通知"}</span>
          </div>
        </article>)}
        {!payoutFocus.length ? <p className="admin-muted">目前沒有畢業後派發紀錄。</p> : null}
      </div>
    </div>
  </section>;
}

function PostGraduationCard({ title, badge, tone = "muted", nextStep, children }: { title: string; badge: string; tone?: DecisionTone; nextStep: string; children: ReactNode }) {
  return <article className="member-step-card">
    <div className="member-step-card-head">
      <div><span>POST STAGE</span><h3>{title}</h3></div>
      <span className={`status-pill ${tone}`}>{badge}</span>
    </div>
    {children}
    <p className={tone === "danger" ? "member-next-step danger" : "member-next-step"}>{nextStep}</p>
  </article>;
}

const NEWBIE_FAILURE_STEP_OPTIONS = [
  { value: "step1", label: "第 1 關 入盟啟動" },
  { value: "step2", label: "第 2 關 回盟補給" },
  { value: "step3", label: "第 3 關 二存加碼" },
  { value: "step4", label: "第 4 關 入盟試煉" },
  { value: "step5", label: "第 5 關 三存加碼" },
  { value: "step6", label: "第 6 關 新人啟航紅包" },
  { value: "step7", label: "第 7 關 聚耀畢業" }
];

const NEWBIE_FAILURE_TYPE_OPTIONS = [
  { value: "channel_failed", label: "渠道 / 身分需協助" },
  { value: "activation_deposit_not_eligible", label: "入盟啟動筆需確認" },
  { value: "second_deposit_not_eligible", label: "二存條件需確認" },
  { value: "third_deposit_not_eligible", label: "三存條件需確認" },
  { value: "quiz_failed", label: "答題協助" },
  { value: "timeout", label: "長期未完成需協助" },
  { value: "review_rejected", label: "審核需人工確認" },
  { value: "admin_canceled", label: "客服取消流程" },
  { value: "other", label: "其他協助" }
];

const NEWBIE_RESCUE_ACTION_OPTIONS = [
  { value: "reopen_step", label: "依原關卡流程處理" },
  { value: "allow_resubmit", label: "允許重新送審" },
  { value: "manual_review_required", label: "轉人工確認" },
  { value: "admin_note_only", label: "僅記錄處理備註" }
];

function NewbieRescueSection({ account, records, activeOperationKey, requestOperation }: {
  account: string;
  records: NewbieFailureRecordRow[];
  activeOperationKey: string;
  requestOperation: (operation: PendingMemberOperation) => void;
}) {
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [localError, setLocalError] = useState("");
  const [failureForm, setFailureForm] = useState({
    step_code: "step4",
    failure_type: "quiz_failed",
    failure_reason: "",
    failed_at: datetimeLocalNowValue(),
    admin_note: ""
  });
  const [attemptForms, setAttemptForms] = useState<Record<number, { open: boolean; rescue_action: string; rescue_reason: string; admin_note: string }>>({});
  const latest = latestNewbieFailureRecord(records);
  const focus = currentNewbieFailureFocus(records);
  const actionableRecords = records.filter((row) => newbieFailureCanRescue(row));
  const historyRecords = records.filter((row) => !newbieFailureCanRescue(row));
  const stepOverviewRows = NEWBIE_FAILURE_STEP_OPTIONS.map((step) => ({
    step,
    record: latestNewbieFailureRecordForStep(records, step.value)
  }));

  function patchAttemptForm(id: number, patch: Partial<{ open: boolean; rescue_action: string; rescue_reason: string; admin_note: string }>) {
    setAttemptForms((prev) => {
      const current = prev[id] ?? {
        open: false,
        rescue_action: "admin_note_only",
        rescue_reason: "",
        admin_note: ""
      };
      return {
        ...prev,
        [id]: { ...current, ...patch }
      };
    });
  }

  function submitFailureRecord() {
    setLocalError("");
    const reason = failureForm.failure_reason.trim();
    if (!reason) {
      setLocalError("請先填寫協助原因。");
      return;
    }
    requestOperation({
      key: "newbie-failure-create",
      title: "確認建立關卡協助紀錄？",
      message: "此操作只會建立客服協助紀錄，不會自動重開關卡、不會自動建立獎勵，也不會自動派發點數。",
      confirmText: "確認建立",
      success: "已建立關卡協助紀錄。",
      action: async () => {
        await api('/api/admin/members/' + encodeURIComponent(account) + '/newbie-failures', {
          method: "POST",
          headers: adminHeaders(),
          body: JSON.stringify({
            step_code: failureForm.step_code,
            failure_type: failureForm.failure_type,
            failure_reason: reason,
            failed_at: datetimeLocalToIso(failureForm.failed_at),
            admin_note: failureForm.admin_note.trim() || null,
            created_by: "admin"
          })
        });
        setFailureForm({
          step_code: "step4",
          failure_type: "quiz_failed",
          failure_reason: "",
          failed_at: datetimeLocalNowValue(),
          admin_note: ""
        });
        setShowCreateForm(false);
      }
    });
  }

  function submitRescueAttempt(record: NewbieFailureRecordRow) {
    setLocalError("");
    const form = attemptForms[record.id] ?? { open: false, rescue_action: "admin_note_only", rescue_reason: "", admin_note: "" };
    const reason = form.rescue_reason.trim();
    if (!reason) {
      setLocalError("請先填寫處理原因。");
      return;
    }
    requestOperation({
      key: memberActionKey("newbie-rescue-attempt", record.id),
      title: "確認新增客服處理紀錄？",
      message: "此操作只會新增客服處理紀錄，不會限制會員闖關次數，也不會改變新人七關進度。確認要新增嗎？",
      confirmText: "確認新增",
      success: "已新增客服處理紀錄。",
      action: async () => {
        await api('/api/admin/newbie-failures/' + record.id + '/rescue-attempts', {
          method: "POST",
          headers: adminHeaders(),
          body: JSON.stringify({
            rescue_action: form.rescue_action,
            rescue_reason: reason,
            admin_note: form.admin_note.trim() || null,
            created_by: "admin"
          })
        });
        patchAttemptForm(record.id, { open: false, rescue_reason: "", admin_note: "" });
      }
    });
  }

  function renderRecord(record: NewbieFailureRecordRow, mode: "active" | "history") {
    const canHandle = newbieFailureCanRescue(record);
    const disabledReason = newbieFailureDisabledReason(record);
    const attemptForm = attemptForms[record.id] ?? { open: false, rescue_action: "admin_note_only", rescue_reason: "", admin_note: "" };
    const operationKey = memberActionKey("newbie-rescue-attempt", record.id);
    return <article className="newbie-rescue-record" key={record.id}>
      <div className="member-reward-card-head">
        <div>
          <span>{mode === "active" ? "目前可協助" : "協助歷史"} #{record.id}</span>
          <h3>{newbieStepLabel(record.step_code)}</h3>
        </div>
        <span className={"status-pill " + newbieFailureTone(record)}>{newbieFailureStatusLabel(record)}</span>
      </div>
      <div className="member-detail-kv-grid">
        <DetailItem label="協助類型" value={newbieFailureTypeLabel(record.failure_type)} />
        <DetailItem label="協助原因" value={cleanFailureReason(record.failure_reason)} />
        <DetailItem label="建立時間" value={shortDate(record.created_at)} />
        <DetailItem label="管理備註" value={cleanAdminNote(record.admin_note)} />
      </div>
      <p className={canHandle ? "member-next-step" : "member-next-step danger"}>{canHandle ? newbieFailureNextStep(record) : disabledReason}</p>
      <div className="inline-actions newbie-rescue-record-actions">
        {canHandle ? <button
          type="button"
          className="admin-btn admin-btn-action"
          disabled={activeOperationKey === operationKey}
          onClick={() => patchAttemptForm(record.id, { open: !attemptForm.open })}
        >
          {activeOperationKey === operationKey ? "處理中..." : "新增客服處理紀錄"}
        </button> : <button type="button" className="admin-btn admin-btn-disabled" disabled>{disabledReason}</button>}
      </div>
      {attemptForm.open && canHandle ? <form className="newbie-rescue-form compact" onSubmit={(event) => { event.preventDefault(); submitRescueAttempt(record); }}>
        <label>處理動作
          <select value={attemptForm.rescue_action} onChange={(event) => patchAttemptForm(record.id, { rescue_action: event.target.value })}>
            {NEWBIE_RESCUE_ACTION_OPTIONS.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
          </select>
        </label>
        <label className="wide">處理原因
          <textarea value={attemptForm.rescue_reason} onChange={(event) => patchAttemptForm(record.id, { rescue_reason: event.target.value })} placeholder="請填寫客服這次如何協助處理。" />
        </label>
        <label className="wide">管理備註
          <textarea value={attemptForm.admin_note} onChange={(event) => patchAttemptForm(record.id, { admin_note: event.target.value })} placeholder="選填，補充內部處理備註。" />
        </label>
        <div className="wide inline-actions">
          <button type="submit" className="admin-btn admin-btn-primary" disabled={activeOperationKey === operationKey}>
            {activeOperationKey === operationKey ? "處理中..." : "送出處理紀錄"}
          </button>
          <button type="button" className="admin-btn admin-btn-secondary" onClick={() => patchAttemptForm(record.id, { open: false })}>取消</button>
        </div>
      </form> : null}
      <details className="raw-details member-card-details">
        <summary>展開明細</summary>
        <dl>
          <div><dt>協助紀錄 ID</dt><dd>#{record.id}</dd></div>
          <div><dt>協助關卡</dt><dd>{newbieStepLabel(record.step_code)}</dd></div>
          <div><dt>協助類型</dt><dd>{newbieFailureTypeLabel(record.failure_type)}</dd></div>
          <div><dt>協助類型原始值</dt><dd>{displayDashText(record.failure_type)}</dd></div>
          <div><dt>問題發生時間</dt><dd>{shortDate(record.failed_at)}</dd></div>
          <div><dt>相關審核 ID</dt><dd>{record.related_review_id ?? "-"}</dd></div>
          <div><dt>相關獎勵 ID</dt><dd>{record.related_reward_id ?? "-"}</dd></div>
          <div><dt>相關存款 ID</dt><dd>{record.related_deposit_id ?? "-"}</dd></div>
          <div><dt>建立人員</dt><dd>{displayDashText(record.created_by)}</dd></div>
          <div><dt>更新時間</dt><dd>{shortDate(record.updated_at)}</dd></div>
        </dl>
        <div className="newbie-rescue-attempt-list">
          <h4>客服處理紀錄</h4>
          {record.attempts?.length ? record.attempts.map((attempt) => <article className="newbie-rescue-attempt" key={attempt.id}>
            <div className="member-detail-kv-grid">
              <DetailItem label="處理序號" value={"第 " + attempt.rescue_no + " 筆"} />
              <DetailItem label="處理動作" value={newbieRescueActionLabel(attempt.rescue_action)} />
              <DetailItem label="處理原因" value={cleanRescueReason(attempt.rescue_reason)} />
              <DetailItem label="建立時間" value={shortDate(attempt.created_at)} />
              <DetailItem label="操作人" value={displayDashText(attempt.created_by)} />
            </div>
            <details className="raw-details">
              <summary>查看原始資料</summary>
              <p className="admin-muted">原始資料僅供工程查錯使用，客服日常可忽略。</p>
              <pre>{prettyRawJson(attempt)}</pre>
            </details>
          </article>) : <p className="admin-muted">目前沒有客服處理紀錄。</p>}
        </div>
        <details className="raw-details">
          <summary>查看原始資料</summary>
          <p className="admin-muted">原始資料僅供工程查錯使用，客服日常可忽略。</p>
          <pre>{prettyRawJson(record)}</pre>
        </details>
      </details>
    </article>;
  }

  function openCreateFormForStep(stepCode: string) {
    setFailureForm((form) => ({ ...form, step_code: stepCode }));
    setShowCreateForm(true);
    window.setTimeout(() => {
      document.getElementById("member-newbie-failure-create-form")?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 0);
  }

  return <section id="member-newbie-rescue-section" className="admin-card newbie-rescue-section">
    <div className="admin-card-head">
      <span>NEWBIE ASSISTANCE</span>
      <h2>新人關卡客服協助紀錄</h2>
    </div>
    <div className="newbie-rescue-notice">
      <strong>客服內部協助紀錄</strong>
      <p>新人七關不限天數完成。此區只記錄客服針對某一關的特殊協助，不代表玩家失敗，也不會自動重開關卡、建立獎勵或派發點數。</p>
      <p>玩家回來時請從目前未完成的關卡繼續；已完成關卡不可重開、不可重領、不可重複派發。一般尚未二存、尚未三存、尚未開券或答題答錯，都是等待條件，不是失敗。</p>
    </div>
    {localError ? <p className="error-box">{localError}</p> : null}
    <h3 className="admin-subtitle">協助總覽</h3>
    <div className="member-detail-kv-grid">
      <DetailItem label="目前協助狀態" value={focus ? newbieFailureStatusLabel(focus) : "目前沒有協助紀錄"} />
      <DetailItem label="最近協助關卡" value={latest ? newbieStepLabel(latest.step_code) : "-"} />
      <DetailItem label="協助類型" value={focus ? newbieFailureTypeLabel(focus.failure_type) : "-"} />
      <DetailItem label="協助原因" value={focus ? cleanFailureReason(focus.failure_reason) : "-"} />
      <DetailItem label="建立時間" value={focus ? shortDate(focus.created_at) : "-"} />
      <DetailItem label="下一步" value={focus ? newbieFailureNextStep(focus) : "目前沒有需要客服處理的協助紀錄。"} />
    </div>

    <h3 className="admin-subtitle">1～7 關協助總覽</h3>
    <div className="newbie-rescue-step-overview">
      {stepOverviewRows.map(({ step, record }) => {
        const canHandle = record ? newbieFailureCanRescue(record) : false;
        return <article className="newbie-rescue-step-card" key={step.value}>
          <div className="newbie-rescue-step-head">
            <div>
              <strong>{step.label}</strong>
              <span>{record ? cleanFailureReason(record.failure_reason) : "目前沒有協助紀錄"}</span>
            </div>
            <span className={"status-pill " + (record ? newbieFailureTone(record) : "muted")}>{record ? newbieFailureStatusLabel(record) : "沒有協助"}</span>
          </div>
          <div className="member-detail-kv-grid">
            <DetailItem label="協助類型" value={record ? newbieFailureTypeLabel(record.failure_type) : "-"} />
            <DetailItem label="建立時間" value={record ? shortDate(record.created_at) : "-"} />
            <DetailItem label="下一步" value={record ? newbieFailureNextStep(record) : "目前沒有協助紀錄。"} />
          </div>
          {!record ? <button type="button" className="admin-btn admin-btn-action mini-button" onClick={() => openCreateFormForStep(step.value)}>建立協助紀錄</button> : canHandle ? <span className="admin-muted">可在「目前可協助」新增客服處理紀錄。</span> : <span className="admin-muted">{newbieFailureDisabledReason(record)}</span>}
        </article>;
      })}
    </div>

    <div className="newbie-rescue-actions">
      <button type="button" className="admin-btn admin-btn-secondary" onClick={() => setShowCreateForm((value) => !value)}>
        {showCreateForm ? "收合建立表單" : "建立關卡協助紀錄"}
      </button>
    </div>

    {showCreateForm ? <form id="member-newbie-failure-create-form" className="newbie-rescue-form" onSubmit={(event) => { event.preventDefault(); submitFailureRecord(); }}>
      <label>協助關卡
        <select value={failureForm.step_code} onChange={(event) => setFailureForm((form) => ({ ...form, step_code: event.target.value }))}>
          {NEWBIE_FAILURE_STEP_OPTIONS.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
        </select>
      </label>
      <label>協助類型
        <select value={failureForm.failure_type} onChange={(event) => setFailureForm((form) => ({ ...form, failure_type: event.target.value }))}>
          {NEWBIE_FAILURE_TYPE_OPTIONS.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
        </select>
      </label>
      <label>問題發生時間
        <input type="datetime-local" value={failureForm.failed_at} onChange={(event) => setFailureForm((form) => ({ ...form, failed_at: event.target.value }))} />
      </label>
      <label className="wide">協助原因
        <textarea value={failureForm.failure_reason} onChange={(event) => setFailureForm((form) => ({ ...form, failure_reason: event.target.value }))} placeholder="請填寫客服可讀的協助原因。" />
      </label>
      <label className="wide">管理備註
        <textarea value={failureForm.admin_note} onChange={(event) => setFailureForm((form) => ({ ...form, admin_note: event.target.value }))} placeholder="選填，補充內部處理備註。" />
      </label>
      <div className="wide inline-actions">
        <button type="submit" className="admin-btn admin-btn-primary" disabled={activeOperationKey === "newbie-failure-create"}>
          {activeOperationKey === "newbie-failure-create" ? "處理中..." : "建立協助紀錄"}
        </button>
        <button type="button" className="admin-btn admin-btn-secondary" onClick={() => setShowCreateForm(false)}>取消</button>
      </div>
    </form> : null}

    <h3 className="admin-subtitle">目前可協助</h3>
    <div className="newbie-rescue-record-list">
      {actionableRecords.map((record) => renderRecord(record, "active"))}
      {!actionableRecords.length ? <p className="admin-muted">目前沒有需要客服處理的協助紀錄。</p> : null}
    </div>
    <h3 className="admin-subtitle">協助歷史</h3>
    <div className="newbie-rescue-record-list">
      {historyRecords.map((record) => renderRecord(record, "history"))}
      {!historyRecords.length ? <p className="admin-muted">目前沒有協助歷史。</p> : null}
    </div>
  </section>;
}

function MemberStatusActions({ detail, activeOperationKey, onConfirmChannel }: { detail: MemberDetail; activeOperationKey: string; onConfirmChannel: () => void }) {
  const channelConfirmed = Boolean(detail.member.channel_confirmed_at) || ["channel_confirmed", "newbie_active", "graduated"].includes(detail.member.status);
  const isConfirming = activeOperationKey === "confirm-channel";
  const advice = memberStatusActionAdvice(detail);
  return <section className="admin-card"><div className="admin-card-head"><span>MEMBER ACTIONS</span><h2>會員狀態操作</h2></div><div className="summary-list">
    <p>目前狀態：{detail.member.status_label}</p>
    <p>目前渠道：{channelConfirmed ? "已確認" : "尚未確認"}</p>
    <p>建議操作：{advice}</p>
  </div><div className="cta-row"><button className="admin-btn admin-btn-primary" disabled={channelConfirmed || isConfirming} onClick={onConfirmChannel}>{isConfirming ? "處理中..." : channelConfirmed ? "渠道已確認" : "確認渠道"}</button><button className="admin-btn admin-btn-disabled" disabled>標記渠道不符</button><button className="admin-btn admin-btn-disabled" disabled>標記異常</button></div></section>;
}

function NewbieQuestRecord({ detail }: { detail: MemberDetail }) {
  const p = detail.newbie_progress;
  const step6Draw = (detail.newbie_red_packet_draws ?? []).find((row) => row.reward_id === detail.step6_reward?.id) ?? (detail.newbie_red_packet_draws ?? []).find((row) => row.voucher_type === "newbie_launch_red_packet");
  const rows: NewbieStepCardRow[] = [
    newbieStepRow({
      step: "第 1 關",
      name: "入盟啟動 70%",
      status: stepStatus(detail.step1_reward, p?.step1_status),
      rewardSummary: detail.step1_reward ? money(detail.step1_reward.amount) : "入盟啟動筆 70%",
      linkedSummary: rewardLink(detail.step1_reward, p?.step1_reward_id),
      completedAt: shortDate(p?.step1_completed_at),
      reward: detail.step1_reward,
      nextStep: detail.step1_reward ? giftHelperNextStep(detail.step1_reward) : "等待選定入盟啟動筆或建立派發紀錄。",
      details: [["派發 ID", rewardLink(detail.step1_reward, p?.step1_reward_id)], ["贈禮小幫手", giftHelperSummary(detail.step1_reward)], ["完成時間", shortDate(p?.step1_completed_at)]],
      raw: detail.step1_reward
    }),
    newbieStepRow({
      step: "第 2 關",
      name: "回盟補給 10%",
      status: stepStatus(detail.step2_reward, p?.step2_status),
      rewardSummary: detail.step2_reward ? money(detail.step2_reward.amount) : "回盟補給 10%",
      linkedSummary: rewardLink(detail.step2_reward, p?.step2_reward_id),
      completedAt: shortDate(p?.step2_completed_at),
      reward: detail.step2_reward,
      nextStep: detail.step2_reward ? giftHelperNextStep(detail.step2_reward) : "等待第 2 關派發紀錄建立。",
      details: [["派發 ID", rewardLink(detail.step2_reward, p?.step2_reward_id)], ["贈禮小幫手", giftHelperSummary(detail.step2_reward)], ["完成時間", shortDate(p?.step2_completed_at)]],
      raw: detail.step2_reward
    }),
    newbieStepRow({
      step: "第 3 關",
      name: "二存加碼 80%",
      status: stepStatus(detail.step3_reward, p?.step3_status),
      rewardSummary: detail.step3_reward ? `${money(detail.step3_reward.amount)} / 寶石 +1` : "二存 80% / 寶石 +1",
      linkedSummary: `${rewardLink(detail.step3_reward, p?.step3_reward_id)} / 存款 ${p?.step3_second_deposit_id ?? p?.step3_deposit_id ?? detail.step3_reward?.source_deposit_id ?? "-"}`,
      completedAt: shortDate(p?.step3_completed_at),
      reward: detail.step3_reward,
      nextStep: detail.step3_reward ? giftHelperNextStep(detail.step3_reward) : "等待選定二存加碼筆。",
      details: [["使用存款 ID", p?.step3_second_deposit_id ?? p?.step3_deposit_id ?? detail.step3_reward?.source_deposit_id ?? "-"], ["寶石碎片", p?.step3_gem_granted_at ? "+1（已給）" : "-"], ["贈禮小幫手", giftHelperSummary(detail.step3_reward)], ["完成時間", shortDate(p?.step3_completed_at)]],
      raw: { progress: p, reward: detail.step3_reward }
    }),
    newbieStepRow({
      step: "第 4 關",
      name: "入盟試煉 10%",
      status: stepStatus(detail.step4_reward, p?.step4_status),
      rewardSummary: detail.step4_reward ? money(detail.step4_reward.amount) : "問答通過 10%",
      linkedSummary: rewardLink(detail.step4_reward, p?.step4_reward_id),
      completedAt: shortDate(p?.step4_completed_at),
      reward: detail.step4_reward,
      nextStep: detail.step4_reward ? giftHelperNextStep(detail.step4_reward) : "等待第 4 關派發紀錄建立。",
      details: [["派發 ID", rewardLink(detail.step4_reward, p?.step4_reward_id)], ["贈禮小幫手", giftHelperSummary(detail.step4_reward)], ["完成時間", shortDate(p?.step4_completed_at)]],
      raw: detail.step4_reward
    }),
    newbieStepRow({
      step: "第 5 關",
      name: "三存加碼 50%",
      status: stepStatus(detail.step5_reward, p?.step5_status),
      rewardSummary: detail.step5_reward ? `${money(detail.step5_reward.amount)} / 寶石 +1 / 新人啟航紅包券 +1` : "三存 50% / 寶石 +1 / 券 +1",
      linkedSummary: `${rewardLink(detail.step5_reward, p?.step5_reward_id)} / 存款 ${p?.step5_deposit_id ?? detail.step5_reward?.source_deposit_id ?? "-"}`,
      completedAt: shortDate(p?.step5_completed_at),
      reward: detail.step5_reward,
      nextStep: detail.step5_reward ? giftHelperNextStep(detail.step5_reward) : "等待選定三存進階筆。",
      details: [["使用存款 ID", p?.step5_deposit_id ?? detail.step5_reward?.source_deposit_id ?? "-"], ["寶石碎片", p?.step5_gem_granted_at ? "+1（已給）" : "-"], ["新人啟航紅包券", p?.step6_voucher_id ? `+1（券 ID ${p.step6_voucher_id}）` : "+1"], ["贈禮小幫手", giftHelperSummary(detail.step5_reward)], ["完成時間", shortDate(p?.step5_completed_at)]],
      raw: { progress: p, reward: detail.step5_reward }
    }),
    newbieStepRow({
      step: "第 6 關",
      name: "新人啟航紅包",
      status: stepStatus(detail.step6_reward, p?.step6_status),
      rewardSummary: detail.step6_reward ? `${money(detail.step6_reward.amount)} / 寶石 +1` : "發券後等待開券抽獎",
      linkedSummary: `${rewardLink(detail.step6_reward, p?.step6_reward_id)} / 抽獎 ${p?.step6_draw_record_id ?? step6Draw?.id ?? "-"}`,
      completedAt: shortDate(p?.step6_completed_at),
      reward: detail.step6_reward,
      nextStep: detail.step6_reward ? giftHelperNextStep(detail.step6_reward) : "紅包券本身不是點數派發；開券抽中河幣後才會進派發中心。",
      details: [["紅包券 ID", p?.step6_voucher_id ?? "-"], ["抽獎紀錄 ID", p?.step6_draw_record_id ?? step6Draw?.id ?? "-"], ["抽中金額", detail.step6_reward ? money(detail.step6_reward.amount) : step6Draw ? money(step6Draw.prize_amount) : "-"], ["贈禮小幫手", giftHelperSummary(detail.step6_reward)], ["第 7 關開放時間", shortDate(p?.step7_available_at)]],
      raw: { progress: p, reward: detail.step6_reward, draw: step6Draw }
    }),
    newbieStepRow({
      step: "第 7 關",
      name: "聚耀畢業",
      status: step7Status(detail),
      rewardSummary: step7RewardSummary(detail),
      linkedSummary: `${rewardLink(detail.step7_final10_reward, p?.step7_final10_reward_id)} / ${rewardLink(detail.step7_graduation_reward, p?.step7_graduation_reward_id)}`,
      completedAt: shortDate(p?.step7_completed_at ?? detail.member.graduation_at),
      reward: detail.step7_final10_reward ?? detail.step7_graduation_reward,
      nextStep: step7NextStepText(detail),
      details: [["最後 10%", detail.step7_final10_reward ? `#${detail.step7_final10_reward.id} / ${money(detail.step7_final10_reward.amount)} / ${rewardStatusLabel(detail.step7_final10_reward.reward_status)} / ${giftHelperSummary(detail.step7_final10_reward)}` : "-"], ["畢業禮", detail.step7_graduation_reward ? `#${detail.step7_graduation_reward.id} / ${money(detail.step7_graduation_reward.amount)} / ${rewardStatusLabel(detail.step7_graduation_reward.reward_status)} / ${giftHelperSummary(detail.step7_graduation_reward)}` : "-"], ["閃耀值", p?.step7_glory_granted_at ? "+5（已給）" : "-"], ["畢業時間", shortDate(detail.member.graduation_at ?? p?.step7_completed_at)]],
      raw: { progress: p, final10: detail.step7_final10_reward, gift: detail.step7_graduation_reward }
    })
  ];
  return <section className="admin-card"><div className="admin-card-head"><span>NEWBIE QUEST</span><h2>新人七關總進度</h2></div>
    <div className="member-step-grid">
      {rows.map((row) => <article className="member-step-card" key={row.step}>
        <div className="member-step-card-head">
          <div><span>{row.step}</span><h3>{row.name}</h3></div>
          <span className={rewardStatusBadgeClass(row.reward?.reward_status ?? row.status)}>{row.status}</span>
        </div>
        <div className="member-detail-kv-grid">
          <DetailItem label="獎勵摘要" value={row.rewardSummary} />
          <DetailItem label="派發 / 關聯" value={row.linkedSummary} />
          <DetailItem label="贈禮任務" value={giftHelperSummary(row.reward)} />
          <DetailItem label="完成時間" value={row.completedAt} />
        </div>
        <p className="member-next-step">{row.nextStep}</p>
        <details className="raw-details member-card-details">
          <summary>展開明細</summary>
          <dl>{row.details.map(([label, value]) => <div key={label}><dt>{label}</dt><dd>{value}</dd></div>)}</dl>
          <details className="raw-details">
            <summary>查看原始資料</summary>
            <p className="admin-muted">原始資料僅供工程查錯使用，客服日常可忽略。</p>
            <pre>{prettyRawJson(row.raw)}</pre>
          </details>
        </details>
      </article>)}
    </div>
  </section>;
}

interface NewbieStepCardRow {
  step: string;
  name: string;
  status: string;
  rewardSummary: ReactNode;
  linkedSummary: ReactNode;
  completedAt: string;
  reward?: RewardRow | null;
  nextStep: string;
  details: Array<[string, ReactNode]>;
  raw: unknown;
}

function newbieStepRow(row: NewbieStepCardRow) {
  return row;
}

function NewbieMilestones({ detail }: { detail: MemberDetail }) {
  const p = detail.newbie_progress;
  const step6Draw = (detail.newbie_red_packet_draws ?? []).find((row) => row.reward_id === detail.step6_reward?.id) ?? (detail.newbie_red_packet_draws ?? []).find((row) => row.voucher_type === "newbie_launch_red_packet");
  return <div className="admin-milestone-grid">
    <MilestoneCard title="第 3 關｜二存加碼" items={[
      ["使用存款 ID", p?.step3_second_deposit_id ?? p?.step3_deposit_id ?? detail.step3_reward?.source_deposit_id ?? "-"],
      ["派發金額", detail.step3_reward ? money(detail.step3_reward.amount) : "-"],
      ["派發狀態", detail.step3_reward ? rewardStatusLabel(detail.step3_reward.reward_status) : "-"],
      ["寶石碎片", p?.step3_gem_granted_at ? "+1（已給）" : "-"],
      ["完成時間", shortDate(p?.step3_completed_at)]
    ]} />
    <MilestoneCard title="第 5 關｜三存加碼" items={[
      ["使用存款 ID", p?.step5_deposit_id ?? detail.step5_reward?.source_deposit_id ?? "-"],
      ["派發金額", detail.step5_reward ? money(detail.step5_reward.amount) : "-"],
      ["派發狀態", detail.step5_reward ? rewardStatusLabel(detail.step5_reward.reward_status) : "-"],
      ["寶石碎片", p?.step5_gem_granted_at ? "+1（已給）" : "-"],
      ["新人啟航紅包券", p?.step6_voucher_id ? `+1（券 ID ${p.step6_voucher_id}）` : "+1"],
      ["完成時間", shortDate(p?.step5_completed_at)]
    ]} />
    <MilestoneCard title="第 6 關｜新人啟航紅包" items={[
      ["紅包券 ID", p?.step6_voucher_id ?? "-"],
      ["抽獎紀錄 ID", p?.step6_draw_record_id ?? step6Draw?.id ?? "-"],
      ["抽中金額", detail.step6_reward ? money(detail.step6_reward.amount) : step6Draw ? money(step6Draw.prize_amount) : "-"],
      ["派發狀態", detail.step6_reward ? rewardStatusLabel(detail.step6_reward.reward_status) : "-"],
      ["寶石碎片", p?.step6_gem_granted_at ? "+1（已給）" : "-"],
      ["第 7 關開放時間", shortDate(p?.step7_available_at)]
    ]} />
    <MilestoneCard title="第 7 關｜聚耀畢業" items={[
      ["首存最後 10%", detail.step7_final10_reward ? `#${detail.step7_final10_reward.id} / ${money(detail.step7_final10_reward.amount)} / ${rewardStatusLabel(detail.step7_final10_reward.reward_status)}` : "-"],
      ["畢業禮", detail.step7_graduation_reward ? `#${detail.step7_graduation_reward.id} / ${money(detail.step7_graduation_reward.amount)} / ${rewardStatusLabel(detail.step7_graduation_reward.reward_status)}` : "-"],
      ["閃耀值", p?.step7_glory_granted_at ? "+5（已給）" : "-"],
      ["畢業時間", shortDate(detail.member.graduation_at ?? p?.step7_completed_at)],
      ["目前狀態", detail.member.status === "graduated" ? "graduated / 正式成員" : detail.member.status_label]
    ]} />
  </div>;
}

function MilestoneCard({ title, items }: { title: string; items: Array<[string, ReactNode]> }) {
  return <article className="admin-milestone-card"><h3>{title}</h3><dl>{items.map(([label, value]) => <div key={label}><dt>{label}</dt><dd>{value}</dd></div>)}</dl></article>;
}

function DepositsSection({ detail, activeOperationKey, onSelectActivationDeposit, onSelectStep3Deposit, onSelectStep5Deposit }: { detail: MemberDetail; activeOperationKey: string; onSelectActivationDeposit: (depositId: number) => void; onSelectStep3Deposit: (depositId: number) => void; onSelectStep5Deposit: (depositId: number) => void }) {
  const p = detail.newbie_progress;
  const channelConfirmed = Boolean(detail.member.channel_confirmed_at) || ["channel_confirmed", "newbie_active", "graduated"].includes(detail.member.status);
  const activationDepositId = detail.member.activation_deposit_id ?? null;
  const step2Done = detail.step2_reward?.reward_status === "completed" || p?.step2_status === "completed";
  const step3Done = detail.step3_reward?.reward_status === "completed" || p?.step3_status === "completed";
  const step3HasReward = Boolean(detail.step3_reward || p?.step3_reward_id);
  const step3DepositId = Number(p?.step3_second_deposit_id ?? p?.step3_deposit_id ?? detail.step3_reward?.source_deposit_id ?? 0) || null;
  const step4Done = detail.step4_reward?.reward_status === "completed" || p?.step4_status === "completed";
  const step5Done = detail.step5_reward?.reward_status === "completed" || p?.step5_status === "completed";
  const step5HasReward = Boolean(detail.step5_reward || p?.step5_reward_id);
  const step5DepositId = Number(p?.step5_deposit_id ?? detail.step5_reward?.source_deposit_id ?? 0) || null;
  const canSelectStep3 = (row: DepositRow) => step2Done && !step3Done && !step3HasReward && Number(row.amount) >= 1000 && row.id !== activationDepositId && row.id !== step5DepositId;
  const canSelectStep5 = (row: DepositRow) => step4Done && !step5Done && !step5HasReward && Number(row.amount) >= 1000 && row.id !== activationDepositId && row.id !== step3DepositId;
  return <section className="admin-card"><div className="admin-card-head"><span>DEPOSITS</span><h2>存款紀錄</h2></div><div className="table-wrap"><table><thead><tr><th>ID</th><th>金額</th><th>時間</th><th>資料來源</th><th>備註</th><th>使用狀態</th><th>操作</th></tr></thead><tbody>{detail.deposits.map((row) => {
    const isActivation = activationDepositId === row.id;
    const isStep3 = step3DepositId === row.id;
    const isStep5 = step5DepositId === row.id;
    const selectableActivation = channelConfirmed && !activationDepositId && Number(row.amount) >= 1000;
    const selectableStep3 = canSelectStep3(row);
    const selectableStep5 = canSelectStep5(row);
    const usedLabel = isActivation ? "入盟啟動筆" : isStep3 ? "第 3 關二存加碼筆" : isStep5 ? "第 5 關三存進階筆" : Number(row.amount) < 1000 ? "未滿 1,000" : "尚未使用";
    const activationKey = memberActionKey("select-activation", row.id);
    const step3Key = memberActionKey("select-step3", row.id);
    const step5Key = memberActionKey("select-step5", row.id);
    return <tr key={row.id}><td>{row.id}</td><td>{money(row.amount)}</td><td>{shortDate(row.deposited_at)}</td><td>{depositSourceLabel(row)}</td><td>{row.note ?? "-"}</td><td>{usedLabel}</td><td>{isActivation || isStep3 || isStep5 ? <span className="status-pill success">已選定</span> : selectableActivation ? <button className="admin-btn admin-btn-action" disabled={activeOperationKey === activationKey} onClick={() => onSelectActivationDeposit(row.id)}>{activeOperationKey === activationKey ? "處理中..." : "選為入盟啟動筆"}</button> : selectableStep3 ? <button className="admin-btn admin-btn-action" disabled={activeOperationKey === step3Key} onClick={() => onSelectStep3Deposit(row.id)}>{activeOperationKey === step3Key ? "處理中..." : "選為第 3 關二存加碼筆"}</button> : selectableStep5 ? <button className="admin-btn admin-btn-action" disabled={activeOperationKey === step5Key} onClick={() => onSelectStep5Deposit(row.id)}>{activeOperationKey === step5Key ? "處理中..." : "選為第 5 關三存進階筆"}</button> : <span className="admin-muted">-</span>}</td></tr>;
  })}{!detail.deposits.length ? <tr><td colSpan={7}>目前沒有存款紀錄</td></tr> : null}</tbody></table></div></section>;
}

function VipNewbieSpecialSection({ detail, onCreate }: { detail: MemberDetail; onCreate: (payload: VipNewbieSpecialPayload) => Promise<VipNewbieSpecialResult> }) {
  const deposits = detail.deposits ?? [];
  const isGraduated = detail.member.status === "graduated";
  const [selectedDeposits, setSelectedDeposits] = useState({ activation: "", second: "", third: "" });
  const [reason, setReason] = useState({ activation: "", second: "", third: "" });
  const [confirmed, setConfirmed] = useState({ activation: false, second: false, third: false });
  const [localError, setLocalError] = useState("");
  const [result, setResult] = useState<VipNewbieSpecialResult | null>(null);
  const [pendingPayload, setPendingPayload] = useState<VipNewbieSpecialPayload | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const activationExistingDepositId = vipDepositIdFromProgressOrRewards(
    detail,
    ["newbie_step1_70", "newbie_step2_10", "newbie_step4_quiz_10", "newbie_step7_final_10"],
    "activation"
  );
  const secondExistingDepositId = vipDepositIdFromProgressOrRewards(detail, ["newbie_step3_second_deposit_80"], "second");
  const thirdExistingDepositId = vipDepositIdFromProgressOrRewards(detail, ["newbie_step5_third_deposit_50"], "third");
  const activationContextId = activationExistingDepositId ?? numberOrNull(selectedDeposits.activation);
  const secondContextId = secondExistingDepositId ?? numberOrNull(selectedDeposits.second);

  const activationStage = buildVipStage(detail, "activation", "首存特批", activationExistingDepositId, numberOrNull(selectedDeposits.activation), [
    ["newbie_step1_70", "首存 70%", 0.7],
    ["newbie_step2_10", "首存回盟補給 10%", 0.1],
    ["newbie_step4_quiz_10", "入盟試煉 10%", 0.1],
    ["newbie_step7_final_10", "首存最後 10%", 0.1]
  ]);
  const secondStage = buildVipStage(detail, "second", "二存特批", secondExistingDepositId, numberOrNull(selectedDeposits.second), [
    ["newbie_step3_second_deposit_80", "二存 80%", 0.8]
  ]);
  const thirdStage = buildVipStage(detail, "third", "三存特批", thirdExistingDepositId, numberOrNull(selectedDeposits.third), [
    ["newbie_step5_third_deposit_50", "三存 50%", 0.5]
  ]);

  async function submitStage(stage: "activation" | "second" | "third") {
    setLocalError("");
    setResult(null);
    if (isGraduated) {
      setLocalError("會員已畢業，不可建立大客新人特批。");
      return;
    }
    const note = reason[stage].trim();
    if (!note) {
      setLocalError("請填寫特批原因。");
      return;
    }
    if (!confirmed[stage]) {
      setLocalError("請勾選確認系統只會建立派發紀錄，仍需到派發中心確認。");
      return;
    }

    let payload: VipNewbieSpecialPayload | null = null;
    if (stage === "activation") {
      const activationId = numberOrNull(selectedDeposits.activation);
      if (!activationId) {
        setLocalError("請選擇首存 deposit。");
        return;
      }
      if (activationStage.isComplete) {
        setLocalError("此段特批已建立，未新增派發紀錄。");
        return;
      }
      payload = { mode: "activation_only", activation_deposit_id: activationId, reason: note, confirm_ready_only: true };
    }
    if (stage === "second") {
      const secondId = numberOrNull(selectedDeposits.second);
      if (!activationContextId) {
        setLocalError("請先建立首存特批，再追加二存。");
        return;
      }
      if (!secondId) {
        setLocalError("請選擇二存 deposit。");
        return;
      }
      if (secondStage.isComplete) {
        setLocalError("此段特批已建立，未新增派發紀錄。");
        return;
      }
      payload = { mode: "activation_second", activation_deposit_id: activationContextId, second_deposit_id: secondId, reason: note, confirm_ready_only: true };
    }
    if (stage === "third") {
      const thirdId = numberOrNull(selectedDeposits.third);
      if (!activationContextId || !secondContextId) {
        setLocalError("請先建立首存與二存特批，再追加三存。");
        return;
      }
      if (!thirdId) {
        setLocalError("請選擇三存 deposit。");
        return;
      }
      if (thirdStage.isComplete) {
        setLocalError("此段特批已建立，未新增派發紀錄。");
        return;
      }
      payload = {
        mode: "activation_second_third",
        activation_deposit_id: activationContextId,
        second_deposit_id: secondContextId,
        third_deposit_id: thirdId,
        reason: note,
        confirm_ready_only: true
      };
    }
    if (!payload) return;
    setPendingPayload(payload);
  }

  async function confirmCreateVipSpecial() {
    if (!pendingPayload) return;
    setSubmitting(true);
    try {
      const response = await onCreate(pendingPayload);
      setResult(response);
      setPendingPayload(null);
    } catch {
      // Parent handler already shows the API error. Keep the dialog open so the user can cancel or retry.
    } finally {
      setSubmitting(false);
    }
  }

  const setStageDeposit = (stage: "activation" | "second" | "third", value: string) => {
    setSelectedDeposits((current) => ({ ...current, [stage]: value }));
  };
  const setStageReason = (stage: "activation" | "second" | "third", value: string) => {
    setReason((current) => ({ ...current, [stage]: value }));
  };
  const setStageConfirmed = (stage: "activation" | "second" | "third", value: boolean) => {
    setConfirmed((current) => ({ ...current, [stage]: value }));
  };

  return <section className="admin-card"><div className="admin-card-head"><span>VIP NEWBIE SPECIAL</span><h2>大客特批 / 專屬新人禮</h2></div>
    <div className="summary-list">
      <p>分段追加首存、二存、三存派發紀錄。既有派發紀錄不會重複建立，仍需到派發中心確認後才會完成入帳。</p>
      <p>本功能不會自動畢業、不會建立第 6 關紅包、不會建立畢業禮。</p>
      {isGraduated ? <p className="admin-muted">會員已畢業，不可建立大客新人特批。</p> : null}
      {localError ? <p className="error-box">{localError}</p> : null}
    </div>
    <div className="admin-milestone-grid">
      <VipSpecialStageCard
        stage={activationStage}
        selectLabel="首存 deposit"
        deposits={vipSelectableDeposits(deposits, [])}
        selectedDepositId={selectedDeposits.activation}
        onSelectDeposit={(value) => setStageDeposit("activation", value)}
        reason={reason.activation}
        onReasonChange={(value) => setStageReason("activation", value)}
        confirmed={confirmed.activation}
        onConfirmedChange={(value) => setStageConfirmed("activation", value)}
        onSubmit={() => submitStage("activation")}
        disabled={isGraduated}
        submitting={submitting}
        submitLabel="建立首存 100% 派發紀錄"
      />
      <VipSpecialStageCard
        stage={secondStage}
        selectLabel="二存 deposit"
        deposits={vipSelectableDeposits(deposits, [activationContextId])}
        selectedDepositId={selectedDeposits.second}
        onSelectDeposit={(value) => setStageDeposit("second", value)}
        reason={reason.second}
        onReasonChange={(value) => setStageReason("second", value)}
        confirmed={confirmed.second}
        onConfirmedChange={(value) => setStageConfirmed("second", value)}
        onSubmit={() => submitStage("second")}
        disabled={isGraduated || !activationContextId}
        submitting={submitting}
        submitLabel="追加二存 80% 派發紀錄"
        blockedMessage={!activationContextId ? "請先建立首存特批。" : undefined}
      />
      <VipSpecialStageCard
        stage={thirdStage}
        selectLabel="三存 deposit"
        deposits={vipSelectableDeposits(deposits, [activationContextId, secondContextId])}
        selectedDepositId={selectedDeposits.third}
        onSelectDeposit={(value) => setStageDeposit("third", value)}
        reason={reason.third}
        onReasonChange={(value) => setStageReason("third", value)}
        confirmed={confirmed.third}
        onConfirmedChange={(value) => setStageConfirmed("third", value)}
        onSubmit={() => submitStage("third")}
        disabled={isGraduated || !activationContextId || !secondContextId}
        submitting={submitting}
        submitLabel="追加三存 50% 派發紀錄"
        blockedMessage={!activationContextId || !secondContextId ? "請先建立首存與二存特批。" : undefined}
      />
    </div>
    {result ? <div className="result"><strong>{result.message ?? "大客特批處理完成"}</strong>
      <div className="member-reward-list">
        {result.rewards.map((row) => <article className="member-reward-mini-card" key={`${row.reward_type}-${row.id}`}>
          <div><strong>{rewardTypeLabel(row.reward_type)}</strong><span>派發 #{row.id}</span></div>
          <div className="member-reward-mini-meta">
            <span>{money(row.amount)}</span>
            <span>{rewardStatusLabel(row.reward_status)}</span>
            <span>{row.source_deposit_id ? `來源存款 #${row.source_deposit_id}` : "來源存款 -"}</span>
            <span>{row.created ? "本次新增" : "已存在 / 未新增"}</span>
          </div>
        </article>)}
      </div>
      <p>請到派發中心確認；系統不會直接標記完成。</p>
    </div> : null}
    <ConfirmDialog
      open={Boolean(pendingPayload)}
      title="確認建立大客特批派發？"
      message="系統會依目前選定的存款與特批原因建立新人派發紀錄；符合條件時會排入贈禮小幫手派發任務。請確認資料無誤後再繼續。"
      confirmText="確認建立"
      cancelText="取消"
      loading={submitting}
      onCancel={() => {
        if (!submitting) setPendingPayload(null);
      }}
      onConfirm={confirmCreateVipSpecial}
    />
  </section>;
}

function LegacyVipNewbieSpecialSection({ detail, onCreate }: { detail: MemberDetail; onCreate: (payload: VipNewbieSpecialPayload) => Promise<VipNewbieSpecialResult> }) {
  const [mode, setMode] = useState<VipNewbieSpecialMode>("activation_only");
  const [activationDepositId, setActivationDepositId] = useState("");
  const [secondDepositId, setSecondDepositId] = useState("");
  const [thirdDepositId, setThirdDepositId] = useState("");
  const [reason, setReason] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [localError, setLocalError] = useState("");
  const [result, setResult] = useState<VipNewbieSpecialResult | null>(null);
  const isGraduated = detail.member.status === "graduated";
  const needsSecond = mode === "activation_second" || mode === "activation_second_third";
  const needsThird = mode === "activation_second_third";
  const deposits = detail.deposits ?? [];
  const activationDeposit = deposits.find((row) => row.id === Number(activationDepositId));
  const secondDeposit = deposits.find((row) => row.id === Number(secondDepositId));
  const thirdDeposit = deposits.find((row) => row.id === Number(thirdDepositId));
  const preview = [
    ...vipPreviewRows(activationDeposit, [
      ["newbie_step1_70", "首存 70%", 0.7],
      ["newbie_step2_10", "首存回盟補給 10%", 0.1],
      ["newbie_step4_quiz_10", "入盟試煉 10%", 0.1],
      ["newbie_step7_final_10", "首存最後 10%", 0.1]
    ]),
    ...(needsSecond ? vipPreviewRows(secondDeposit, [["newbie_step3_second_deposit_80", "二存加碼 80%", 0.8]]) : []),
    ...(needsThird ? vipPreviewRows(thirdDeposit, [["newbie_step5_third_deposit_50", "三存加碼 50%", 0.5]]) : [])
  ];

  const previewWithStatus = preview.map((row) => ({
    ...row,
    existingReward: findVipPreviewExistingReward(detail.rewards, row.reward_type, row.deposit_id)
  }));
  const allPreviewRowsSelected = preview.length > 0 && preview.every((row) => Boolean(row.deposit_id));
  const allPreviewRewardsExist = allPreviewRowsSelected && previewWithStatus.every((row) => Boolean(row.existingReward));

  async function submit() {
    setLocalError("");
    setResult(null);
    if (isGraduated) {
      setLocalError("會員已畢業，不可建立大客新人特批。");
      return;
    }
    if (!activationDepositId) {
      setLocalError("請選擇首存 / 入盟啟動筆。");
      return;
    }
    if (needsSecond && !secondDepositId) {
      setLocalError("此模式必須選擇二存。");
      return;
    }
    if (needsThird && !thirdDepositId) {
      setLocalError("此模式必須選擇三存。");
      return;
    }
    if (!reason.trim()) {
      setLocalError("請填寫特批原因。");
      return;
    }
    if (!confirmed) {
      setLocalError("請先勾選確認，了解系統只會建立派發紀錄，仍需到派發中心確認。");
      return;
    }

    if (allPreviewRewardsExist) {
      setLocalError("此組特批已建立，不能重複建立。");
      return;
    }

    const response = await onCreate({
      mode,
      activation_deposit_id: Number(activationDepositId),
      second_deposit_id: needsSecond ? Number(secondDepositId) : null,
      third_deposit_id: needsThird ? Number(thirdDepositId) : null,
      reason: reason.trim(),
      confirm_ready_only: true
    });
    setResult(response);
  }

  return <section className="admin-card"><div className="admin-card-head"><span>VIP NEWBIE SPECIAL</span><h2>大客特批 / 專屬新人禮</h2></div>
    <div className="summary-list">
      <p>後台人工選定同日首存 / 二存 / 三存，系統只建立新人派發紀錄；仍需到派發中心確認後才會完成入帳。</p>
      <p>此工具不會自動畢業、不會自動抽紅包、不會自動開畢業首週或週通行證。</p>
      {isGraduated ? <p className="admin-muted">會員已畢業，不可建立大客新人特批。</p> : null}
      {allPreviewRewardsExist ? <p className="admin-muted">此組特批已建立，不能重複建立。</p> : null}
      {localError ? <p className="error-box">{localError}</p> : null}
    </div>
    <div className="admin-overview-grid">
      <label className="admin-overview-card"><span>特批模式</span><select value={mode} onChange={(event) => setMode(event.target.value as VipNewbieSpecialMode)}>
        <option value="activation_only">只特批首存</option>
        <option value="activation_second">特批首存 + 二存</option>
        <option value="activation_second_third">特批首存 + 二存 + 三存</option>
      </select><small>二存 / 三存必須人工選擇，不自動抓第 2 / 第 3 筆。</small></label>
      <DepositSelectCard label="首存 / 入盟啟動筆" value={activationDepositId} onChange={setActivationDepositId} deposits={deposits} required />
      {needsSecond ? <DepositSelectCard label="二存" value={secondDepositId} onChange={setSecondDepositId} deposits={deposits} required /> : null}
      {needsThird ? <DepositSelectCard label="三存" value={thirdDepositId} onChange={setThirdDepositId} deposits={deposits} required /> : null}
    </div>
    <h3 className="admin-subtitle">派發紀錄預覽</h3>
    <div className="table-wrap"><table><thead><tr><th>派發類型</th><th>說明</th><th>存款</th><th>預估金額</th><th>建立狀態</th></tr></thead><tbody>
      {previewWithStatus.map((row) => <tr key={`${row.reward_type}-${row.deposit_id ?? "none"}`}><td>{rewardTypeLabel(row.reward_type)}</td><td>{row.label}</td><td>{row.deposit_id ? `#${row.deposit_id}` : "待選"}</td><td>{row.amount ? money(row.amount) : "-"}</td><td>{row.existingReward ? `已存在 #${row.existingReward.id} / ${rewardStatusLabel(row.existingReward.reward_status)}` : "將建立派發紀錄"}</td></tr>)}
      {!preview.length ? <tr><td colSpan={5}>請先選擇存款</td></tr> : null}
    </tbody></table></div>
    <div className="admin-inline-form">
      <label>特批原因<textarea value={reason} onChange={(event) => setReason(event.target.value)} rows={3} placeholder="請輸入大客同日連續充值特批原因" /></label>
      <label className="admin-checkbox-line"><input type="checkbox" checked={confirmed} onChange={(event) => setConfirmed(event.target.checked)} />我已確認這些存款可用於大客特批，系統只會建立派發紀錄，仍需到派發中心確認。</label>
      <button disabled={isGraduated || allPreviewRewardsExist} onClick={submit}>建立大客特批派發紀錄</button>
    </div>
    {result ? <div className="result"><strong>建立結果</strong><div className="table-wrap"><table><thead><tr><th>ID</th><th>派發類型</th><th>金額</th><th>狀態</th><th>來源存款</th><th>結果</th></tr></thead><tbody>
      {result.rewards.map((row) => <tr key={`${row.reward_type}-${row.id}`}><td>#{row.id}</td><td>{rewardTypeLabel(row.reward_type)}</td><td>{money(row.amount)}</td><td>{rewardStatusLabel(row.reward_status)}</td><td>{row.source_deposit_id ? `#${row.source_deposit_id}` : "-"}</td><td>{row.created ? "新建立" : "已存在 / 未新增"}</td></tr>)}
    </tbody></table></div><p>請至派發中心確認；此工具不會直接標記完成。</p></div> : null}
  </section>;
}

interface VipStageView {
  key: "activation" | "second" | "third";
  title: string;
  deposit: DepositRow | null;
  rows: Array<{
    reward_type: string;
    label: string;
    deposit_id: number | null;
    amount: number;
    existingReward: RewardRow | null;
  }>;
  isComplete: boolean;
}

function VipSpecialStageCard({
  stage,
  selectLabel,
  deposits,
  selectedDepositId,
  onSelectDeposit,
  reason,
  onReasonChange,
  confirmed,
  onConfirmedChange,
  onSubmit,
  disabled,
  submitting,
  submitLabel,
  blockedMessage
}: {
  stage: VipStageView;
  selectLabel: string;
  deposits: DepositRow[];
  selectedDepositId: string;
  onSelectDeposit: (value: string) => void;
  reason: string;
  onReasonChange: (value: string) => void;
  confirmed: boolean;
  onConfirmedChange: (value: boolean) => void;
  onSubmit: () => void;
  disabled?: boolean;
  submitting?: boolean;
  submitLabel: string;
  blockedMessage?: string;
}) {
  if (stage.isComplete) {
    return <article className="admin-milestone-card"><h3>{stage.title}</h3>
      <VipStageSummary stage={stage} />
      <p className="member-next-step">此段特批已建立，不可重複建立。</p>
      <VipStageRows rows={stage.rows} />
      <button className="admin-btn admin-btn-done" disabled>{stage.title}已建立</button>
    </article>;
  }

  return <article className="admin-milestone-card"><h3>{stage.title}</h3>
    <VipStageSummary stage={stage} />
    <p className="member-next-step">{blockedMessage ?? "尚未建立，可追加。"}</p>
    <label>{selectLabel}<select value={selectedDepositId} onChange={(event) => onSelectDeposit(event.target.value)} disabled={Boolean(blockedMessage)}>
      <option value="">請選擇 deposit</option>
      {deposits.map((deposit) => <option key={deposit.id} value={deposit.id}>#{deposit.id}｜{money(deposit.amount)}｜{shortDate(deposit.deposited_at)}</option>)}
    </select></label>
    <VipStageRows rows={stage.rows} />
    <label>特批原因<textarea value={reason} onChange={(event) => onReasonChange(event.target.value)} rows={3} placeholder="請填寫大客特批原因" disabled={Boolean(blockedMessage)} /></label>
    <label className="admin-checkbox-line"><input type="checkbox" checked={confirmed} onChange={(event) => onConfirmedChange(event.target.checked)} disabled={Boolean(blockedMessage)} />我已確認此段存款可用於大客特批，系統只會建立派發紀錄，仍需到派發中心確認。</label>
    <button className="admin-btn admin-btn-action" disabled={Boolean(disabled || blockedMessage || !selectedDepositId || submitting)} onClick={onSubmit}>{submitting ? "處理中..." : submitLabel}</button>
  </article>;
}

function VipStageSummary({ stage }: { stage: VipStageView }) {
  const rewardCount = stage.rows.filter((row) => row.existingReward).length;
  return <div className="member-detail-kv-grid">
    <DetailItem label="存款 ID" value={stage.deposit ? `#${stage.deposit.id}` : "待選"} />
    <DetailItem label="存款金額" value={stage.deposit ? money(stage.deposit.amount) : "-"} />
    <DetailItem label="存款時間" value={stage.deposit ? shortDate(stage.deposit.deposited_at) : "-"} />
    <DetailItem label="已建立派發" value={`${rewardCount} 筆`} />
  </div>;
}

function VipStageRows({ rows }: { rows: VipStageView["rows"] }) {
  return <div className="member-reward-list">
    {rows.map((row) => <article className="member-reward-mini-card" key={row.reward_type}>
      <div>
        <strong>{rewardTypeLabel(row.reward_type)}</strong>
        <span>{row.label}</span>
      </div>
      <div className="member-reward-mini-meta">
        <span>{row.amount ? money(row.amount) : "-"}</span>
        <span>{row.existingReward ? `派發 #${row.existingReward.id}` : "將建立派發紀錄"}</span>
        <span>{row.existingReward ? rewardStatusLabel(row.existingReward.reward_status) : "尚未建立"}</span>
        <span>{giftHelperSummary(row.existingReward)}</span>
      </div>
    </article>)}
  </div>;
}

function buildVipStage(
  detail: MemberDetail,
  key: "activation" | "second" | "third",
  title: string,
  existingDepositId: number | null,
  selectedDepositId: number | null,
  specs: Array<[string, string, number]>
): VipStageView {
  const depositId = existingDepositId ?? selectedDepositId;
  const deposit = depositId ? detail.deposits.find((row) => row.id === depositId) ?? null : null;
  const rows = specs.map(([reward_type, label, percent]) => {
    const existingReward = findVipPreviewExistingReward(detail.rewards, reward_type, depositId);
    return {
      reward_type,
      label,
      deposit_id: depositId,
      amount: deposit ? Math.floor(Number(deposit.amount) * 100 * percent) : Number(existingReward?.amount ?? 0),
      existingReward
    };
  });
  return {
    key,
    title,
    deposit,
    rows,
    isComplete: Boolean(existingDepositId) && rows.every((row) => Boolean(row.existingReward))
  };
}

function vipDepositIdFromProgressOrRewards(
  detail: MemberDetail,
  rewardTypes: string[],
  stage: "activation" | "second" | "third"
) {
  if (stage === "activation") {
    const progressId = Number(detail.newbie_progress?.activation_deposit_id ?? detail.member.activation_deposit_id ?? 0) || null;
    if (progressId) return progressId;
  }
  if (stage === "second") {
    const progressId = Number(detail.newbie_progress?.step3_second_deposit_id ?? detail.step3_reward?.source_deposit_id ?? 0) || null;
    if (progressId) return progressId;
  }
  if (stage === "third") {
    const progressId = Number(detail.newbie_progress?.step5_deposit_id ?? detail.step5_reward?.source_deposit_id ?? 0) || null;
    if (progressId) return progressId;
  }
  const reward = detail.rewards.find((row) => rewardTypes.includes(row.reward_type) && row.reward_status !== "canceled" && row.source_deposit_id);
  return Number(reward?.source_deposit_id ?? 0) || null;
}

function numberOrNull(value: string) {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

function vipStageOverview(stage: VipStageView, expectedCount: number, canAppend = true) {
  const existingCount = stage.rows.filter((row) => row.existingReward).length;
  if (stage.isComplete) return `已建立 ${existingCount} 筆派發紀錄`;
  if (!canAppend) return "尚未建立";
  return expectedCount > 1 ? `尚未建立｜需 ${expectedCount} 筆` : "尚未建立｜可追加";
}

function vipStageDepositNote(stage: VipStageView) {
  if (stage.deposit) return vipDepositText(stage.deposit);
  return stage.isComplete ? "已建立" : "請選擇 deposit";
}

function vipDepositText(deposit: DepositRow) {
  return `deposit #${deposit.id}｜${money(deposit.amount)}｜${shortDate(deposit.deposited_at)}`;
}

function vipSelectableDeposits(deposits: DepositRow[], excludeIds: Array<number | null>) {
  const excluded = new Set(excludeIds.filter((id): id is number => Boolean(id)));
  return deposits.filter((deposit) => Number(deposit.amount) >= 1000 && !excluded.has(deposit.id));
}

function DepositSelectCard({ label, value, onChange, deposits, required }: { label: string; value: string; onChange: (value: string) => void; deposits: DepositRow[]; required?: boolean }) {
  return <label className="admin-overview-card"><span>{label}{required ? "（必選）" : ""}</span><select value={value} onChange={(event) => onChange(event.target.value)}>
    <option value="">請選擇存款</option>
    {deposits.map((deposit) => <option key={deposit.id} value={deposit.id}>#{deposit.id}｜{money(deposit.amount)}｜{shortDate(deposit.deposited_at)}</option>)}
  </select><small>後端會再次檢查會員、金額、同日與是否已用於其他新人關卡。</small></label>;
}

function vipPreviewRows(deposit: DepositRow | undefined, specs: Array<[string, string, number]>) {
  return specs.map(([reward_type, label, percent]) => ({
    reward_type,
    label,
    deposit_id: deposit?.id ?? null,
    amount: deposit ? Math.floor(Number(deposit.amount) * 100 * percent) : 0
  }));
}

function findVipPreviewExistingReward(rewards: RewardRow[], rewardType: string, depositId: number | null) {
  if (!depositId) return null;
  return rewards.find((reward) =>
    reward.reward_type === rewardType &&
    reward.reward_status !== "canceled" &&
    Number(reward.source_deposit_id ?? 0) === depositId
  ) ?? null;
}

function RegularRebateSectionV2({ detail, activeOperationKey, onCreate }: { detail: MemberDetail; activeOperationKey: string; onCreate: (depositId: number, manualConfirm?: { manual_confirm: boolean; manual_confirm_note: string }) => void }) {
  const rows = detail.regular_rebate_candidates ?? [];
  const isGraduated = detail.member.status === "graduated" && Boolean(detail.member.graduation_at);
  const [manualNotes, setManualNotes] = useState<Record<number, string>>({});
  const [manualChecked, setManualChecked] = useState<Record<number, boolean>>({});
  const [localError, setLocalError] = useState("");

  function submitManual(row: RegularRebateCandidateRow) {
    const note = (manualNotes[row.deposit_id] ?? "").trim();
    if (!manualChecked[row.deposit_id]) {
      setLocalError("請先勾選已確認此筆存款未重複領取其他同筆回饋。");
      return;
    }
    if (!note) {
      setLocalError("人工確認備註必填。");
      return;
    }
    setLocalError("");
    onCreate(row.deposit_id, { manual_confirm: true, manual_confirm_note: note });
  }

  return <section className="admin-card"><div className="admin-card-head"><span>REGULAR REBATE</span><h2>正式成員常態回饋</h2></div>
    <div className="summary-list">
      <p>後台人工建立正式成員單筆充值回饋，系統會自行計算比例與派發金額。</p>
      {!isGraduated ? <p className="admin-muted">會員尚未畢業，不能建立常態回饋。</p> : null}
      {localError ? <p className="error-box">{localError}</p> : null}
    </div>
    <div className="table-wrap"><table><thead><tr>
      <th>存款 ID</th><th>存款金額</th><th>存款時間</th><th>回饋比例</th><th>預計派發</th><th>狀態</th><th>不可用原因</th><th>人工確認原因</th><th>已有派發</th><th>操作</th>
    </tr></thead><tbody>{rows.map((row) => <tr key={row.deposit_id}>
      <td>{row.deposit_id}</td>
      <td>{money(row.deposit_amount)}</td>
      <td>{shortDate(row.deposited_at)}</td>
      <td>{formatPercent(row.rebate_percent)}</td>
      <td>{row.reward_amount ? money(row.reward_amount) : "-"}</td>
      <td>{regularRebateStatusText(row)}</td>
      <td>{row.unavailable_reasons?.length ? row.unavailable_reasons.join("、") : "-"}</td>
      <td>{row.manual_confirm_reasons?.length ? row.manual_confirm_reasons.join("、") : "-"}</td>
      <td>{row.existing_reward_id ? `#${row.existing_reward_id} / ${rewardStatusLabel(row.existing_reward_status)}` : "-"}</td>
      <td>{row.status === "eligible" ? <button className="admin-btn admin-btn-action" disabled={activeOperationKey === memberActionKey("regular-rebate", row.deposit_id)} onClick={() => onCreate(row.deposit_id)}>{activeOperationKey === memberActionKey("regular-rebate", row.deposit_id) ? "處理中..." : "建立常態回饋派發"}</button> : row.status === "manual_review" ? <div className="admin-inline-form">
        <label className="admin-checkbox-line"><input type="checkbox" checked={Boolean(manualChecked[row.deposit_id])} onChange={(event) => setManualChecked((current) => ({ ...current, [row.deposit_id]: event.target.checked }))} />我已確認此筆存款未重複領取其他同筆回饋</label>
        <textarea value={manualNotes[row.deposit_id] ?? ""} onChange={(event) => setManualNotes((current) => ({ ...current, [row.deposit_id]: event.target.value }))} placeholder="請填寫人工確認備註" rows={3} />
        <button className="admin-btn admin-btn-action" disabled={activeOperationKey === memberActionKey("regular-rebate-manual", row.deposit_id)} onClick={() => submitManual(row)}>{activeOperationKey === memberActionKey("regular-rebate-manual", row.deposit_id) ? "處理中..." : "人工確認後建立"}</button>
      </div> : row.status_label}</td>
    </tr>)}{!rows.length ? <tr><td colSpan={10}>目前沒有可評估的存款</td></tr> : null}</tbody></table></div>
  </section>;
}

function WeekendBonusSection({ rows }: { rows: WeekendBonusProgressRow[] }) {
  return <section className="admin-card"><div className="admin-card-head"><span>WEEKEND BONUS</span><h2>週末任務與發券紀錄</h2></div>
    <div className="summary-list">
      <p>這裡顯示週末任務完成與發出的紅包券。紅包券本身不進派發中心；抽中河幣後才會出現在下方派發紀錄。</p>
    </div>
    <div className="table-wrap"><table><thead><tr>
      <th>週末期間</th><th>打卡</th><th>單筆滿 1,000</th><th>累積滿 5,000</th><th>高級券</th><th>更新時間</th><th>原始資料</th>
    </tr></thead><tbody>{rows.map((row) => <tr key={row.id}>
      <td>{shortDate(row.weekend_start_at)} 至 {shortDate(row.weekend_end_at)}<small className="technical-key">{row.weekend_id}</small></td>
      <td>{row.checkin_completed_at ? `已完成｜${shortDate(row.checkin_completed_at)}` : "未完成"}</td>
      <td>{row.single_1000_completed_at ? `存款 #${row.single_1000_deposit_id ?? "-"}｜券 #${row.single_1000_voucher_id ?? "-"}｜${shortDate(row.single_1000_completed_at)}` : "未完成"}</td>
      <td>{row.cumulative_5000_completed_at ? `${money(row.cumulative_5000_amount ?? 0)}｜存款 ${formatDepositIdList(row.cumulative_5000_deposit_ids_json)}｜${shortDate(row.cumulative_5000_completed_at)}` : "未完成"}</td>
      <td>{row.cumulative_5000_voucher_id ? `券 #${row.cumulative_5000_voucher_id}` : "-"}</td>
      <td>{shortDate(row.updated_at)}</td>
      <td><details className="raw-details"><summary>查看原始資料</summary><pre>{prettyRawJson(row)}</pre></details></td>
    </tr>)}{!rows.length ? <tr><td colSpan={7}>目前沒有週末耀旺紀錄</td></tr> : null}</tbody></table></div>
  </section>;
}

function RegularRebateSection({ detail, onCreate }: { detail: MemberDetail; onCreate: (depositId: number) => void }) {
  const rows = detail.regular_rebate_candidates ?? [];
  const isGraduated = detail.member.status === "graduated" && Boolean(detail.member.graduation_at);
  return <section className="admin-card"><div className="admin-card-head"><span>REGULAR REBATE</span><h2>正式成員常態回饋</h2></div>
    <div className="summary-list">
      <p>後台人工選定正式成員日常充值存款後，系統依級距建立派發單。比例與金額由後端計算。</p>
      {!isGraduated ? <p className="admin-muted">會員尚未畢業，不能建立常態回饋。</p> : null}
    </div>
    <div className="table-wrap"><table><thead><tr>
      <th>存款 ID</th><th>存款金額</th><th>存款時間</th><th>回饋比例</th><th>預計派發</th><th>狀態</th><th>不可用原因</th><th>需人工確認原因</th><th>已有派發</th><th>操作</th>
    </tr></thead><tbody>{rows.map((row) => <tr key={row.deposit_id}>
      <td>{row.deposit_id}</td>
      <td>{money(row.deposit_amount)}</td>
      <td>{shortDate(row.deposited_at)}</td>
      <td>{formatPercent(row.rebate_percent)}</td>
      <td>{row.reward_amount ? money(row.reward_amount) : "-"}</td>
      <td>{regularRebateStatusText(row)}</td>
      <td>{row.unavailable_reasons?.length ? row.unavailable_reasons.join("、") : "-"}</td>
      <td>{row.manual_confirm_reasons?.length ? row.manual_confirm_reasons.join("、") : "-"}</td>
      <td>{row.existing_reward_id ? `#${row.existing_reward_id} / ${rewardStatusLabel(row.existing_reward_status)}` : "-"}</td>
      <td>{row.status === "eligible" ? <button onClick={() => onCreate(row.deposit_id)}>建立常態回饋派發</button> : row.status_label}</td>
    </tr>)}{!rows.length ? <tr><td colSpan={10}>目前沒有可顯示的存款</td></tr> : null}</tbody></table></div>
  </section>;
}

function RewardSummarySection({ rows }: { rows: RewardRow[] }) {
  const [copyMessage, setCopyMessage] = useState("");
  async function copy(row: RewardRow) { await copyRawJson(row); setCopyMessage(`已複製派發原始資料 #${row.id}`); }
  return <section className="admin-card"><div className="admin-card-head"><span>REWARDS</span><h2>派發紀錄摘要</h2></div>
    <div className="summary-list"><p>這裡顯示進入派發中心的河幣派發，並整合贈禮小幫手任務狀態。週末紅包券本身不在此處；若有週末紅包抽獎，這裡顯示的是紅包抽中河幣派發紀錄。</p></div>
    {copyMessage ? <p className="result">{copyMessage}</p> : null}
    <div className="member-reward-summary-grid">
      {rows.map((row) => <article className="member-reward-card" key={row.id}>
        <div className="member-reward-card-head">
          <div>
            <span>派發 #{row.id}</span>
            <h3>{rewardTypeLabel(row.reward_type)}</h3>
            {isRedPacketDrawReward(row.reward_type) ? <small>紅包抽中河幣派發紀錄</small> : null}
          </div>
          <strong>{money(row.amount)}</strong>
        </div>
        <div className="member-detail-kv-grid">
          <DetailItem label="派發狀態" value={rewardStatusLabel(row.reward_status)} />
          <DetailItem label="贈禮任務 ID" value={giftHelperTaskId(row)} />
          <DetailItem label="贈禮狀態" value={giftHelperTaskStatus(row)} />
          <DetailItem label="嘗試次數" value={giftHelperAttemptText(row)} />
          <DetailItem label="建立時間" value={shortDate(row.created_at)} />
          <DetailItem label="完成時間" value={shortDate(row.claimed_at)} />
        </div>
        <p className={giftHelperFailedReason(row) !== "-" ? "member-next-step danger" : "member-next-step"}>{giftHelperFailedReason(row) !== "-" ? `失敗原因：${giftHelperFailedReason(row)}` : giftHelperNextStep(row)}</p>
        <details className="raw-details member-card-details">
          <summary>展開明細</summary>
          <dl>
            <div><dt>派發 ID</dt><dd>#{row.id}</dd></div>
            <div><dt>派發類型</dt><dd>{rewardTypeLabel(row.reward_type)}</dd></div>
            <div><dt>來源存款</dt><dd>{row.source_deposit_id ? `#${row.source_deposit_id}` : "-"}</dd></div>
            <div><dt>贈禮任務</dt><dd>{giftHelperSummary(row)}</dd></div>
            <div><dt>排程時間</dt><dd>{shortDate(row.crawler_dispatch_scheduled_at)}</dd></div>
            <div><dt>最近嘗試</dt><dd>{shortDate(row.crawler_dispatch_last_attempt_at)}</dd></div>
            <div><dt>爬蟲回傳編號</dt><dd>{row.crawler_reference_id ?? "-"}</dd></div>
          </dl>
          <details className="raw-details">
            <summary>查看原始資料</summary>
            <p className="admin-muted">原始資料僅供工程查錯使用，客服日常可忽略。</p>
            <pre>{prettyRawJson(row)}</pre>
          </details>
          <button className="secondary mini-button" onClick={() => copy(row)}>複製原始資料</button>
        </details>
      </article>)}
      {!rows.length ? <p className="admin-muted">目前沒有派發紀錄</p> : null}
    </div>
  </section>;
}

function InviteSection({ rows, unlocked, unlockedAt, inviteCode }: { rows: InviteRelationRow[]; unlocked: boolean; unlockedAt?: string | null; inviteCode?: InviteCodeInfo | null }) {
  const fullInviteLink = inviteCode?.invite_link ? `${window.location.origin}${inviteCode.invite_link}` : "";
  return <section className="admin-card"><div className="admin-card-head"><span>INVITE</span><h2>邀友支線</h2></div><div className="summary-list"><p>邀友支線：{unlocked ? `已開啟，${shortDate(unlockedAt)}` : "尚未開啟"}</p><p>邀請碼：{inviteCode?.invite_code ?? "尚未建立"}</p><p>邀請連結：{fullInviteLink || "-"}</p><p>V1 規則：好友首存滿 1,000，邀請人可建立 50,000 派發單。</p></div><div className="table-wrap"><table><thead><tr><th>好友帳號</th><th>任務</th><th>狀態</th><th>存款 ID</th><th>派發 ID</th><th>派發狀態</th><th>金額</th></tr></thead><tbody>{rows.map((row) => <tr key={row.id}><td>{row.invitee_account}</td><td>{row.phase === "invite_first_deposit" ? "好友首存單筆滿 1,000" : row.phase}</td><td>{inviteRelationStatusLabel(row.status)}</td><td>{row.source_deposit_id ?? "-"}</td><td>{row.reward_id ?? "-"}</td><td>{rewardStatusLabel(row.dispatch_status)}</td><td>{row.reward_amount ? money(row.reward_amount) : "-"}</td></tr>)}{!rows.length ? <tr><td colSpan={7}>目前沒有邀友資料</td></tr> : null}</tbody></table></div></section>;
}

function InviteManualReviewSection({ rows, account }: { rows: InviteManualReviewRow[]; account: string }) {
  const isMostlyInviter = rows.some((row) => row.inviter_account === account);
  const title = isMostlyInviter ? "邀友明細" : "被邀請明細";
  const description = isMostlyInviter
    ? "依好友分組顯示每位好友帶來的入群獎、首存獎、二存獎、三存獎。"
    : "此會員是被邀請好友；獎勵會建立給邀請人，這裡顯示對應邀友獎勵是否已建立或已派發。";
  return <section className="admin-card"><div className="admin-card-head"><span>INVITE MANUAL</span><h2>{title}</h2></div><div className="summary-list"><p>{description}</p></div><div className="table-wrap"><table><thead><tr><th>{isMostlyInviter ? "好友" : "邀請人"}</th><th>LINE / 入群</th><th>充值明細</th><th>對應邀友獎勵</th><th>更新時間</th></tr></thead><tbody>{rows.map((row) => {
    const isInviter = row.inviter_account === account;
    return <tr key={row.id}><td><strong>{isInviter ? `好友：${row.invitee_account}` : `邀請人：${row.inviter_account}`}</strong><br />{isInviter ? `邀請人：${row.inviter_account}` : `好友：${row.invitee_account}`}<br />LINE：{row.line_nickname || "-"}</td><td>{yesNoText("已加 LINE", row.line_added)}<br />{yesNoText("已入群", row.group_joined)}<br />{yesNoText("本線渠道", row.own_channel)}</td><td>首存金額：{amountOrDash(row.first_deposit_amount)}<br />二存金額：{depositAmountText(row.second_deposit_amount, row.second_stage_reward_id)}<br />三存金額：{amountOrDash(row.third_deposit_amount)}</td><td>入群獎 6,666：{rewardStatusRef(row.join_reward_id, row.join_reward_status)}<br />首存獎 50,000：{rewardStatusRef(row.first_deposit_reward_id, row.first_deposit_reward_status)}<br />二存獎 30,000：{rewardStatusRef(row.second_stage_reward_id, row.second_stage_reward_status)}<br />三存獎 10,000：{rewardStatusRef(row.third_deposit_reward_id, row.third_deposit_reward_status)}</td><td>{shortDate(row.updated_at)}</td></tr>;
  })}{!rows.length ? <tr><td colSpan={5}>目前沒有人工快審邀友明細</td></tr> : null}</tbody></table></div></section>;
}

function AssetSummarySection({ detail }: { detail: MemberDetail }) {
  const vouchers = detail.assets?.vouchers ?? [];
  return <section className="admin-card"><div className="admin-card-head"><span>ASSETS</span><h2>資產與券摘要</h2></div><div className="admin-overview-grid">
    <InfoCard label="寶石碎片餘額" value={detail.assets?.gems ?? 0} />
    <InfoCard label="閃耀值" value={formatGlory(detail.assets?.glory_value ?? 0)} />
    <InfoCard label="新手加碼紅包券" value={voucherBreakdown(vouchers, "newbie_bonus_red_packet")} />
    <InfoCard label="新人啟航紅包券" value={voucherBreakdown(vouchers, "newbie_launch_red_packet")} />
    <InfoCard label="畢業啟航紅包券" value={voucherBreakdown(vouchers, "graduate_launch_red_packet")} />
    <InfoCard label="高級啟航紅包券" value={voucherBreakdown(vouchers, "advanced_launch_red_packet")} />
    <InfoCard label="週末耀旺紅包券" value={voucherBreakdown(vouchers, "weekend_bright_red_packet")} />
    <InfoCard label="高級週末紅包券" value={voucherBreakdown(vouchers, "weekend_advanced_red_packet")} />
  </div><h3 className="admin-subtitle">資產流水</h3><AssetLedgerTable rows={detail.asset_ledgers ?? []} /><h3 className="admin-subtitle">紅包券交易紀錄</h3><VoucherTransactionTable rows={detail.voucher_transactions ?? []} /><h3 className="admin-subtitle">紅包抽獎紀錄</h3><NewbieRedPacketDrawTable rows={detail.newbie_red_packet_draws ?? []} /></section>;
}

function GuildLevelSection({ detail }: { detail: MemberDetail }) {
  const gloryValue = detail.assets?.glory_value ?? 0;
  const levelInfo = getGuildLevelInfo(gloryValue);
  const gloryRows = (detail.asset_ledgers ?? []).filter((row) => row.asset_type === "glory" || row.asset_type === "glory_value");
  return <section className="admin-card"><div className="admin-card-head"><span>GUILD LEVEL</span><h2>會員身份 / 公會等級</h2></div><div className="summary-list"><p>目前等級：Lv{levelInfo.current.level_number} {levelInfo.current.display_name}</p><p>目前閃耀值：{formatGlory(gloryValue)}</p><p>下一級：{levelInfo.next ? `Lv${levelInfo.next.level_number} ${levelInfo.next.display_name}` : "已達最高等級"}</p><p>距離下一級：{levelInfo.next ? `${formatGlory(levelInfo.remainingToNext)} 閃耀值` : "0"}</p></div><AssetLedgerTable rows={gloryRows} /></section>;
}

function ReviewsSection({ rows }: { rows: ReviewRequestRow[] }) {
  const [copyMessage, setCopyMessage] = useState("");
  async function copy(row: ReviewRequestRow) { await copyRawJson(row); setCopyMessage(`已複製審核原始資料 #${row.id}`); }
  return <section className="admin-card"><div className="admin-card-head"><span>REVIEWS</span><h2>待確認 / 審核紀錄</h2></div>{copyMessage ? <p className="result">{copyMessage}</p> : null}<div className="table-wrap"><table><thead><tr><th>申請編號</th><th>類型</th><th>狀態</th><th>金額</th><th>建立時間</th><th>審核時間</th><th>摘要</th><th>原因</th><th>原始資料</th></tr></thead><tbody>{rows.map((row) => <tr key={row.id}><td>{row.request_no ?? row.id}</td><td>{reviewTypeLabel(row.review_type)}</td><td>{reviewStatusLabel(row.status)}</td><td>{row.amount ? money(row.amount) : "-"}</td><td>{shortDate(row.created_at)}</td><td>{shortDate(row.reviewed_at)}</td><td>{formatReviewRequestSummary(row)}</td><td>{row.reject_reason ?? row.cancel_reason ?? "-"}</td><td><details className="raw-details"><summary>查看原始資料</summary><pre>{prettyRawJson(row)}</pre></details><button className="secondary mini-button" onClick={() => copy(row)}>複製原始資料</button></td></tr>)}{!rows.length ? <tr><td colSpan={9}>目前沒有審核紀錄</td></tr> : null}</tbody></table></div></section>;
}

function LogsSection({ rows }: { rows: AdminLogRow[] }) {
  const [copyMessage, setCopyMessage] = useState("");
  async function copy(row: AdminLogRow) { await copyRawJson(row.payload ?? row); setCopyMessage(`已複製操作原始資料 #${row.id}`); }
  return <section className="admin-card"><div className="admin-card-head"><span>ADMIN LOGS</span><h2>操作紀錄</h2></div>{copyMessage ? <p className="result">{copyMessage}</p> : null}<div className="table-wrap"><table><thead><tr><th>時間</th><th>操作類型</th><th>目標</th><th>操作內容</th><th>原始資料</th></tr></thead><tbody>{rows.map((row) => <tr key={row.id}><td>{shortDate(row.created_at)}</td><td><strong>{actionLabel(row.action)}</strong></td><td>{row.target_id ?? row.target_type}</td><td>{formatAdminLogSummary(row)}</td><td><details className="raw-details"><summary>查看原始資料</summary><pre>{prettyRawJson(row.payload ?? row)}</pre></details><button className="secondary mini-button" onClick={() => copy(row)}>複製原始資料</button></td></tr>)}{!rows.length ? <tr><td colSpan={5}>目前沒有操作紀錄</td></tr> : null}</tbody></table></div></section>;
}

function LocalDevTools({ account, activeOperationKey, requestOperation }: { account: string; activeOperationKey: string; requestOperation: (operation: PendingMemberOperation) => void }) {
  const localActions = [
    {
      key: "local-confirm-channel",
      label: "確認渠道",
      success: "渠道已確認。",
      message: "此操作會在 local/dev 測試環境更新會員渠道狀態。請確認要執行測試操作。",
      action: () => api(`/api/admin/members/${encodeURIComponent(account)}/confirm-channel`, { method: "POST", headers: adminHeaders(), body: JSON.stringify({}) })
    },
    {
      key: "local-unlock-step2",
      label: "測試第 2 關解鎖",
      success: "第 2 關已開啟。",
      message: "此操作會在 local/dev 測試環境開啟第 2 關狀態。請確認要執行測試操作。",
      action: () => api(`/api/admin/members/${encodeURIComponent(account)}/unlock-newbie-step2-local`, { method: "POST", headers: adminHeaders(), body: JSON.stringify({}) })
    },
    {
      key: "local-complete-step2",
      label: "測試第 2 關打卡",
      success: "第 2 關打卡已建立。",
      message: "此操作會在 local/dev 測試環境建立第 2 關打卡紀錄。請確認要執行測試操作。",
      action: () => api(`/api/admin/members/${encodeURIComponent(account)}/complete-step2-checkin`, { method: "POST", headers: adminHeaders(), body: JSON.stringify({}) })
    },
    {
      key: "local-check-step6",
      label: "測試第 6 關紅包",
      success: "第 6 關紅包流程已檢查。",
      message: "此操作會在 local/dev 測試環境檢查第 6 關紅包流程。請確認要執行測試操作。",
      action: () => api(`/api/admin/members/${encodeURIComponent(account)}/check-step6-red-packet`, { method: "POST", headers: adminHeaders(), body: JSON.stringify({}) })
    },
    {
      key: "local-create-step7",
      label: "測試第 7 關產生畢業派發",
      success: "第 7 關畢業派發已建立。",
      message: "此操作會在 local/dev 測試環境建立第 7 關畢業派發。請確認要執行測試操作。",
      action: () => api(`/api/admin/members/${encodeURIComponent(account)}/create-step7-graduation-rewards`, { method: "POST", headers: adminHeaders(), body: JSON.stringify({ force_available: true }) })
    }
  ];
  return <details className="admin-card admin-dev-tools"><summary>local/dev 測試工具</summary><p className="admin-muted">此區只供 local/dev 驗收使用。正式流程請以派發中心與審核中心為準。</p><div className="cta-row">{localActions.map((item) => (
    <button
      key={item.key}
      className="admin-btn admin-btn-secondary"
      disabled={activeOperationKey === item.key}
      onClick={() => requestOperation({
        key: item.key,
        title: "確認執行 local/dev 測試操作？",
        message: item.message,
        confirmText: "確認執行",
        success: item.success,
        action: item.action
      })}
    >
      {activeOperationKey === item.key ? "處理中..." : item.label}
    </button>
  ))}</div></details>;
}

function AssetLedgerTable({ rows }: { rows: AssetLedgerRow[] }) {
  const [copyMessage, setCopyMessage] = useState("");
  async function copy(row: AssetLedgerRow) { await copyRawJson(row); setCopyMessage(`已複製資產流水 #${row.id}`); }
  if (!rows.length) return <p className="admin-muted">目前沒有資產流水。</p>;
  return <div className="table-wrap">{copyMessage ? <p className="result">{copyMessage}</p> : null}<table><thead><tr><th>時間</th><th>類型</th><th>異動</th><th>餘額</th><th>原因</th><th>備註</th><th>原始資料</th></tr></thead><tbody>{rows.map((row) => <tr key={row.id}><td>{shortDate(row.created_at)}</td><td>{assetTypeLabel(row.asset_type)}</td><td>{row.amount}</td><td>{row.balance_after ?? "-"}</td><td>{reasonLabel(row.reason_code)}</td><td>{row.note ?? sourceLabel(row.activity_code)}</td><td><details className="raw-details"><summary>查看原始資料</summary><pre>{prettyRawJson(row)}</pre></details><button className="secondary mini-button" onClick={() => copy(row)}>複製原始資料</button></td></tr>)}</tbody></table></div>;
}

function VoucherTransactionTable({ rows }: { rows: VoucherTransactionRow[] }) {
  const [copyMessage, setCopyMessage] = useState("");
  async function copy(row: VoucherTransactionRow) { await copyRawJson(row); setCopyMessage(`已複製紅包券交易 #${row.id}`); }
  if (!rows.length) return <p className="admin-muted">目前沒有紅包券交易紀錄。</p>;
  return <div className="table-wrap">{copyMessage ? <p className="result">{copyMessage}</p> : null}<table><thead><tr><th>時間</th><th>券種</th><th>動作</th><th>來源</th><th>券 ID</th><th>備註</th><th>原始資料</th></tr></thead><tbody>{rows.map((row) => <tr key={row.id}><td>{shortDate(row.created_at)}</td><td>{voucherTypeLabel(row.voucher_type)}</td><td>{voucherActionLabel(row.action)}</td><td>{sourceLabel(row.activity_code)}</td><td>{row.voucher_id ?? "-"}</td><td>{voucherNoteLabel(row.note)}</td><td><details className="raw-details"><summary>查看原始資料</summary><pre>{prettyRawJson(row)}</pre></details><button className="secondary mini-button" onClick={() => copy(row)}>複製原始資料</button></td></tr>)}</tbody></table></div>;
}

function NewbieRedPacketDrawTable({ rows }: { rows: NewbieRedPacketDrawRow[] }) {
  const [copyMessage, setCopyMessage] = useState("");
  async function copy(row: NewbieRedPacketDrawRow) { await copyRawJson(row); setCopyMessage(`已複製紅包抽獎 #${row.id}`); }
  if (!rows.length) return <p className="admin-muted">目前沒有紅包抽獎紀錄。</p>;
  return <div className="table-wrap">{copyMessage ? <p className="result">{copyMessage}</p> : null}<table><thead><tr><th>時間</th><th>券種</th><th>抽獎編號</th><th>獎項</th><th>派發 ID</th><th>派發狀態</th><th>原始資料</th></tr></thead><tbody>{rows.map((row) => <tr key={row.id}><td>{shortDate(row.created_at)}</td><td>{voucherTypeLabel(row.voucher_type)}</td><td>{row.draw_ref}</td><td>{row.prize_name ?? "紅包"}｜{money(row.prize_amount)}</td><td>{row.reward_id ?? "-"}</td><td>{row.reward_status ? rewardStatusLabel(row.reward_status) : drawStatusLabel(row.status)}</td><td><details className="raw-details"><summary>查看原始資料</summary><pre>{prettyRawJson(row)}</pre></details><button className="secondary mini-button" onClick={() => copy(row)}>複製原始資料</button></td></tr>)}</tbody></table></div>;
}

function voucherTotal(rows?: VoucherSummaryRow[]) { return `${rows?.reduce((sum, row) => sum + Number(row.count ?? 0), 0) ?? 0}`; }
function voucherSummary(rows?: VoucherSummaryRow[]) { if (!rows?.length) return "目前沒有紅包券"; return rows.map((row) => `${voucherTypeLabel(row.voucher_type)} ${voucherStatusLabel(row.status)} ${row.count}`).join("，"); }
function voucherBreakdown(rows: VoucherSummaryRow[], type: string) {
  const available = rows.filter((row) => row.voucher_type === type && row.status === "available").reduce((sum, row) => sum + Number(row.count ?? 0), 0);
  const used = rows.filter((row) => row.voucher_type === type && row.status === "used").reduce((sum, row) => sum + Number(row.count ?? 0), 0);
  const expired = rows.filter((row) => row.voucher_type === type && row.status === "expired").reduce((sum, row) => sum + Number(row.count ?? 0), 0);
  const parts = [`可用 ${available}`];
  if (used) parts.push(`已使用 ${used}`);
  if (expired) parts.push(`已過期 ${expired}`);
  return parts.join(" / ");
}
function voucherBreakdownMany(rows: VoucherSummaryRow[], types: string[]) {
  return types.map((type) => {
    const count = rows.filter((row) => row.voucher_type === type).reduce((sum, row) => sum + Number(row.count ?? 0), 0);
    return count ? `${voucherTypeLabel(type)}：${voucherBreakdown(rows, type)}` : "";
  }).filter(Boolean).join("；");
}
function stepStatus(reward?: RewardRow | null, progressStatus?: string | null) { if (reward) return rewardStatusLabel(reward.reward_status); if (progressStatus === "completed") return "已完成"; if (progressStatus === "ready" || progressStatus === "waiting_dispatch") return "等待派發"; if (progressStatus === "unlocked") return "可進行"; return "尚未完成"; }
function step7Status(detail: MemberDetail) { if (detail.member.status === "graduated" || detail.newbie_progress?.step7_status === "completed") return "已畢業 / 已完成"; if (detail.step7_final10_reward || detail.step7_graduation_reward) return "畢業派發中"; if (detail.newbie_progress?.step7_available_at) return "等待畢業派發"; return "尚未開放"; }
function step7RewardSummary(detail: MemberDetail) { const final10 = detail.step7_final10_reward ? `尾款 ${money(detail.step7_final10_reward.amount)}` : "尾款尚未建立"; const gift = detail.step7_graduation_reward ? `畢業禮 ${money(detail.step7_graduation_reward.amount)}` : "畢業禮尚未建立"; return `${final10} + ${gift}`; }
function rewardLink(reward?: RewardRow | null, fallbackId?: number | null) { const id = reward?.id ?? fallbackId; return id ? `派發 #${id}` : "-"; }
function isRedPacketDrawReward(type: string) { return type.endsWith("_red_packet_draw") || type.includes("red_packet_draw"); }
function formatPercent(value?: number | null) { const next = Number(value ?? 0); return Number.isFinite(next) && next > 0 ? `${Math.round(next * 100)}%` : "-"; }
function displayNode(value: ReactNode) { return value === null || value === undefined || value === "" ? "-" : value; }
function readableText(value: string | null | undefined, fallback: string) {
  const text = String(value ?? "").trim();
  if (!text) return fallback;
  if (/^(undefined|null)$/i.test(text)) return fallback;
  if (text.includes("�")) return fallback;
  const compact = text.replace(/\s/g, "");
  if (compact.length >= 3 && /^[?？]+$/.test(compact)) return fallback;
  const questionCount = [...compact].filter((char) => char === "?" || char === "？").length;
  if (compact.length >= 6 && questionCount / compact.length > 0.6) return fallback;
  return text;
}
function cleanFailureReason(value?: string | null) {
  return readableText(value, "未填寫協助原因");
}
function cleanAdminNote(value?: string | null) {
  return readableText(value, "未填寫管理備註");
}
function cleanRescueReason(value?: string | null) {
  return readableText(value, "未填寫處理原因");
}
function displayDashText(value?: string | number | null) {
  return readableText(value === null || value === undefined ? "" : String(value), "-");
}
function datetimeLocalNowValue() {
  const now = new Date();
  const local = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
}
function datetimeLocalToIso(value: string) {
  if (!value) return null;
  const date = new Date(value);
  return Number.isFinite(date.getTime()) ? date.toISOString() : null;
}
function optionLabel(options: Array<{ value: string; label: string }>, value?: string | null) {
  return options.find((item) => item.value === value)?.label ?? value ?? "-";
}
function newbieStepLabel(value?: string | null) {
  return optionLabel(NEWBIE_FAILURE_STEP_OPTIONS, value);
}
function newbieFailureTypeLabel(value?: string | null) {
  return optionLabel(NEWBIE_FAILURE_TYPE_OPTIONS, value);
}
function newbieRescueActionLabel(value?: string | null) {
  return optionLabel(NEWBIE_RESCUE_ACTION_OPTIONS, value);
}
function latestNewbieFailureRecord(records: NewbieFailureRecordRow[]) {
  return [...records].sort((a, b) => new Date(b.failed_at || b.created_at).getTime() - new Date(a.failed_at || a.created_at).getTime())[0] ?? null;
}
function latestNewbieFailureRecordForStep(records: NewbieFailureRecordRow[], stepCode: string) {
  return latestNewbieFailureRecord(records.filter((row) => row.step_code === stepCode));
}
function currentNewbieFailureFocus(records: NewbieFailureRecordRow[]) {
  const sorted = [...records].sort((a, b) => new Date(b.failed_at || b.created_at).getTime() - new Date(a.failed_at || a.created_at).getTime());
  return sorted.find((row) => newbieFailureCanRescue(row))
    ?? sorted.find((row) => ["exhausted", "expired", "canceled"].includes(newbieFailureComputedStatus(row)))
    ?? sorted[0]
    ?? null;
}
function newbieFailureComputedStatus(row: NewbieFailureRecordRow) {
  if (row.computed_status) return row.computed_status;
  if (row.is_expired) return "expired";
  if (row.is_exhausted) return "exhausted";
  return row.status || "open";
}
function newbieFailureCanRescue(row: NewbieFailureRecordRow) {
  if (row.can_rescue === false) return false;
  const status = newbieFailureComputedStatus(row);
  return row.can_rescue === true || status === "open";
}
function newbieFailureStatusLabel(row: NewbieFailureRecordRow) {
  const status = newbieFailureComputedStatus(row);
  if (status === "open") return Number(row.rescue_count ?? 0) > 0 ? "處理中" : "待客服處理";
  if (status === "resolved") return "已處理";
  if (status === "canceled") return "已取消";
  if (status === "exhausted" || status === "expired") return "待客服確認";
  return row.status_label || status || "-";
}
function newbieFailureTone(row: NewbieFailureRecordRow): DecisionTone {
  const status = newbieFailureComputedStatus(row);
  if (status === "open") return "soon";
  if (status === "resolved") return "success";
  if (status === "canceled") return "muted";
  return "soon";
}
function newbieFailureDisabledReason(row: NewbieFailureRecordRow) {
  const status = newbieFailureComputedStatus(row);
  if (status === "canceled") return "此協助紀錄已取消，請依會員目前未完成關卡判斷是否需另建新紀錄。";
  if (status === "resolved") return "此協助紀錄已處理完成。";
  if (status === "exhausted" || status === "expired") return "此協助紀錄已進入歷史狀態，請依會員目前未完成關卡走既有後台流程處理。";
  return "目前不可新增處理紀錄。";
}
function newbieFailureNextStep(row: NewbieFailureRecordRow) {
  return newbieStepHandlingGuidance(row.step_code);
}
function newbieStepHandlingGuidance(stepCode?: string | null) {
  if (stepCode === "step1") return "確認正確入盟啟動筆；已完成不可重領。";
  if (stepCode === "step2") return "確認回盟補給流程；已完成不可重領。";
  if (stepCode === "step3") return "客服確認正確二存，已完成不可重領，不可使用已用過的 deposit。";
  if (stepCode === "step4") return "第 4 關玩家可重新答題；答錯只提示再試，不算失敗。";
  if (stepCode === "step5") return "客服確認正確三存，已完成不可重領，不可使用已用過的 deposit。";
  if (stepCode === "step6") return "紅包券異常由客服確認，不自動補發券。";
  if (stepCode === "step7") return "客服確認畢業資格，不重複建立畢業禮。";
  return "請依會員目前未完成關卡，用原本後台流程協助處理。";
}
function buildCustomerDecisionSummary(detail: MemberDetail) {
  const channelConfirmed = Boolean(detail.member.channel_confirmed_at) || ["channel_confirmed", "newbie_active", "graduated"].includes(detail.member.status);
  const isGraduated = memberIsGraduated(detail);
  const regularCandidate = latestRegularRebateCandidate(detail.regular_rebate_candidates ?? []);
  const dispatchIssues = dispatchIssueRewards(detail.rewards);
  const failedCount = detail.rewards.filter((row) => row.crawler_dispatch_status === "failed").length;
  const timeoutCount = detail.rewards.filter((row) => isGiftHelperProcessingTimedOut(row)).length;
  const maxAttemptCount = detail.rewards.filter((row) => Number(row.crawler_dispatch_attempt_count ?? 0) >= 3 && row.crawler_dispatch_status !== "completed").length;
  const latestIssue = dispatchIssues[0] ?? null;
  const notifiable = notifiableRewardCandidates(detail.rewards);
  const latestNotifiable = notifiable[0] ?? null;
  const availableVoucherCount = voucherCountByStatus(detail.assets?.vouchers ?? [], "available");
  const redPacketPendingCount = detail.rewards.filter((row) => isRedPacketDrawReward(row.reward_type) && row.reward_status !== "completed").length;
  const pendingReviews = (detail.review_requests ?? []).filter((row) => reviewNeedsHumanAction(row));
  const rejectedReviews = (detail.review_requests ?? []).filter((row) => row.status === "rejected");
  const manualReviewCount = pendingReviews.length;
  const stageTarget: DecisionTarget = isGraduated ? "graduated" : "newbie";
  const stageActionText = isGraduated ? "前往畢業後階段" : "前往新人階段";
  let blockerTarget: DecisionTarget = stageTarget;
  let blockerActionText = stageActionText;

  let stage = "新人階段";
  let stageBadge = "新人中";
  let stageTone: DecisionTone = "soon";
  let stageDetail = getMainProgress(detail);
  if (!channelConfirmed) {
    stage = "異常 / 待確認";
    stageBadge = "渠道待確認";
    stageTone = "danger";
    stageDetail = "渠道尚未確認，可能影響後續領獎與活動判斷。";
  } else if (isGraduated) {
    stage = isWithinGraduateFirstWeek(detail.member.graduation_at) ? "畢業後首週" : "正式會員";
    stageBadge = isWithinGraduateFirstWeek(detail.member.graduation_at) ? "首週中" : "已畢業";
    stageTone = "success";
    stageDetail = `畢業時間：${shortDate(detail.member.graduation_at)}`;
  }

  let blocker = "目前沒有明顯卡點";
  let blockerBadge = "正常";
  let blockerTone: DecisionTone = "success";
  let blockerDetail = "目前沒有需要立即處理的明顯阻塞。";
  let nextStep = "目前沒有需客服處理的事項。";
  let nextStepDetail = "可依會員提問切換到新人階段、畢業後階段或派發紀錄查看。";

  if (!channelConfirmed) {
    blocker = "渠道尚未確認";
    blockerBadge = "需確認";
    blockerTone = "danger";
    blockerDetail = "請先確認會員來源渠道。";
    nextStep = "請先確認渠道來源。";
    nextStepDetail = "渠道確認後再查看新人階段或畢業後福利。";
    blockerTarget = "newbie";
    blockerActionText = "前往新人階段";
  } else if (dispatchIssues.length) {
    blocker = latestIssue ? `${dispatchIssueKind(latestIssue)}：${rewardTypeLabel(latestIssue.reward_type)}` : "有派發異常";
    blockerBadge = "派發異常";
    blockerTone = "danger";
    blockerDetail = latestIssue ? `最近一筆：${rewardBrief(latestIssue)}，請至贈禮小幫手任務處理。` : "請查看派發異常卡片。";
    nextStep = "請至贈禮小幫手派發任務處理。";
    nextStepDetail = "可重新排入、取消任務，或確認外部已處理後標記人工確認完成。";
    blockerTarget = "rewards";
    blockerActionText = "前往派發紀錄";
  } else if (manualReviewCount) {
    blocker = `有 ${manualReviewCount} 筆待審核 / 人工處理`;
    blockerBadge = "待審核";
    blockerTone = "soon";
    blockerDetail = "請切到審核紀錄查看申請內容。";
    nextStep = "請先處理待審核資料。";
    nextStepDetail = "審核通過後若建立 reward，仍會依派發流程處理。";
    blockerTarget = "reviews";
    blockerActionText = "前往審核紀錄";
  } else if (!isGraduated) {
    blocker = `尚未完成新人七關：${newbieCurrentBlocker(detail)}`;
    blockerBadge = "新人中";
    blockerTone = "soon";
    blockerDetail = `目前卡在 ${newbieCurrentBlocker(detail)}，請查看新人階段進度。`;
    nextStep = "請先查看新人階段進度。";
    nextStepDetail = "會員尚未畢業，暫不可建立常態回饋。";
    blockerTarget = "newbie";
    blockerActionText = "前往新人階段";
  } else if (regularCandidate?.status === "unavailable") {
    blocker = "常態回饋目前不可用";
    blockerBadge = "不可建立";
    blockerTone = "locked";
    blockerDetail = regularCandidate.unavailable_reasons?.join("；") || "請查看常態回饋區塊。";
    nextStep = "請查看常態回饋不可用原因。";
    nextStepDetail = "不要手動改規則，依現有資格判斷處理。";
    blockerTarget = "graduated";
    blockerActionText = "前往常態回饋";
  } else if (availableVoucherCount) {
    blocker = `有 ${availableVoucherCount} 張可用紅包券尚未使用`;
    blockerBadge = "可提醒";
    blockerTone = "soon";
    blockerDetail = "玩家可到前台紅包券頁開券。";
    nextStep = "可提醒會員開啟紅包券。";
    nextStepDetail = "開券抽中河幣後才會進入派發流程。";
    blockerTarget = "assets";
    blockerActionText = "前往資產 / 券";
  } else if (notifiable.length) {
    blocker = "有已派發完成獎勵可通知";
    blockerBadge = "可通知";
    blockerTone = "success";
    blockerDetail = latestNotifiable ? `最近一筆：${rewardBrief(latestNotifiable)}，請到可通知會員清單複製話術。` : "請查看可通知會員清單。";
    nextStep = "可通知會員獎勵已派發完成。";
    nextStepDetail = "會員詳情未含通知 log，是否已通知仍以可通知會員清單為準。";
    blockerTarget = "rewards";
    blockerActionText = "前往派發紀錄";
  }
  const nextTarget = blockerTarget;
  const nextActionText = blockerActionText;

  const dispatchIssue = dispatchIssues.length
    ? `失敗 ${failedCount} 筆 / 逾時 ${timeoutCount} 筆 / 最大重試 ${maxAttemptCount} 筆`
    : "目前沒有派發異常";
  const dispatchDetail = latestIssue
    ? `最近一筆：${dispatchIssueKind(latestIssue)}，${rewardBrief(latestIssue)}。下一步：至贈禮小幫手派發任務重新排入或人工確認完成。`
    : "沒有 failed、處理逾時或達最大重試次數的派發。";
  const notifyText = notifiable.length ? `${notifiable.length} 筆可通知候選` : "目前沒有待通知獎勵";
  const notifyDetail = latestNotifiable
    ? `最近一筆：${rewardBrief(latestNotifiable)}。請至已派發可通知會員清單複製話術並標記已通知。`
    : "會員詳情未含通知 log；若需確認已通知狀態，請至可通知會員清單。";
  const assetText = `可用券 ${availableVoucherCount} 張 / 寶石 ${detail.assets?.gems ?? 0}`;
  const assetDetail = redPacketPendingCount
    ? `有 ${redPacketPendingCount} 筆紅包抽中河幣尚未完成派發。`
    : "目前沒有抽中後未完成派發的紅包紀錄。";
  const reviewText = manualReviewCount ? `${manualReviewCount} 筆待審核` : "目前沒有待審核資料";
  const reviewDetail = rejectedReviews.length ? `另有 ${rejectedReviews.length} 筆已拒絕紀錄，可切到審核紀錄查看原因。` : "已通過但未建立派發單的精準判斷資料尚未串接。";

  return {
    stage,
    stageBadge,
    stageTone,
    stageDetail,
    stageTarget,
    stageActionText,
    blocker,
    blockerBadge,
    blockerTone,
    blockerDetail,
    blockerTarget,
    blockerActionText,
    nextStep,
    nextStepDetail,
    nextTarget,
    nextActionText,
    dispatchIssue,
    dispatchBadge: dispatchIssues.length ? "需處理" : "正常",
    dispatchTone: (dispatchIssues.length ? "danger" : "success") as DecisionTone,
    dispatchDetail,
    notifyText,
    notifyBadge: notifiable.length ? "可通知" : "無待通知",
    notifyTone: (notifiable.length ? "success" : "muted") as DecisionTone,
    notifyDetail,
    assetText,
    assetBadge: availableVoucherCount ? "有可用券" : "資產正常",
    assetTone: (redPacketPendingCount ? "soon" : availableVoucherCount ? "good" : "muted") as DecisionTone,
    assetDetail,
    reviewText,
    reviewBadge: manualReviewCount ? "待處理" : "無待審",
    reviewTone: (manualReviewCount ? "soon" : "success") as DecisionTone,
    reviewDetail,
    failedCount,
    timeoutCount,
    maxAttemptCount,
    notifiableCount: notifiable.length,
    availableVoucherCount,
    redPacketPendingCount,
    pendingReviewCount: manualReviewCount
  };
}
function memberIsGraduated(detail: MemberDetail) { return detail.member.status === "graduated" || Boolean(detail.member.graduation_at) || detail.newbie_progress?.step7_status === "completed"; }
function memberStatusActionAdvice(detail: MemberDetail) {
  const channelConfirmed = Boolean(detail.member.channel_confirmed_at) || ["channel_confirmed", "newbie_active", "graduated"].includes(detail.member.status);
  if (dispatchIssueRewards(detail.rewards).length) return "此會員有派發異常，請至贈禮小幫手派發任務處理。";
  if (!channelConfirmed) return "渠道尚未確認，請先確認渠道，才能進入入盟啟動筆選定與後續活動流程。";
  if (!memberIsGraduated(detail)) return "會員仍在新人流程中，請查看新人階段進度與目前卡點。";
  if (isWithinGraduateFirstWeek(detail.member.graduation_at)) return "會員已進入畢業後首週，請查看畢業後階段、首週任務與紅包券狀態。";
  return "會員為正式會員，請查看常態回饋、週通行證、週末任務與派發紀錄。";
}
function postGraduationTabNote(detail: MemberDetail) { return memberIsGraduated(detail) ? `已畢業 ${shortDate(detail.member.graduation_at)}` : "尚未畢業"; }
function newbieCurrentBlocker(detail: MemberDetail) {
  const p = detail.newbie_progress;
  if (!detail.step1_reward && p?.step1_status !== "completed") return "第 1 關";
  if (!detail.step2_reward && p?.step2_status !== "completed") return "第 2 關";
  if (!detail.step3_reward && p?.step3_status !== "completed") return "第 3 關";
  if (!detail.step4_reward && p?.step4_status !== "completed") return "第 4 關";
  if (!detail.step5_reward && p?.step5_status !== "completed") return "第 5 關";
  if (!detail.step6_reward && p?.step6_status !== "completed") return "第 6 關";
  if (!detail.step7_final10_reward || !detail.step7_graduation_reward) return "第 7 關";
  return getMainProgress(detail);
}
function dispatchIssueKind(row: RewardRow) {
  if (isGiftHelperProcessingTimedOut(row)) return "處理逾時";
  if (Number(row.crawler_dispatch_attempt_count ?? 0) >= 3 && row.crawler_dispatch_status !== "completed") return "已達最大重試次數";
  if (row.crawler_dispatch_status === "failed") return "派發失敗";
  return "派發異常";
}
function rewardBrief(row: RewardRow) {
  return `${rewardTypeLabel(row.reward_type)} ${money(row.amount)}（派發 #${row.id}）`;
}
function isWithinGraduateFirstWeek(value?: string | null) {
  if (!value) return false;
  const time = new Date(value).getTime();
  return Number.isFinite(time) && Date.now() - time >= 0 && Date.now() - time <= 7 * 24 * 60 * 60 * 1000;
}
function voucherCountByStatus(rows: VoucherSummaryRow[], status: string) {
  return rows.filter((row) => row.status === status).reduce((sum, row) => sum + Number(row.count ?? 0), 0);
}
function reviewNeedsHumanAction(row: ReviewRequestRow) {
  return ["pending", "waiting", "need_review", "needs_review", "manual_review"].includes(row.status);
}
function notifiableRewardCandidates(rows: RewardRow[]) {
  return rows.filter((row) => row.reward_status === "completed" && row.crawler_dispatch_status === "completed").sort((a, b) => new Date(b.claimed_at || b.created_at).getTime() - new Date(a.claimed_at || a.created_at).getTime());
}
function dispatchIssueRewards(rows: RewardRow[]) {
  return rows.filter((row) => row.crawler_dispatch_status === "failed" || isGiftHelperProcessingTimedOut(row) || (Number(row.crawler_dispatch_attempt_count ?? 0) >= 3 && row.crawler_dispatch_status !== "completed")).sort((a, b) => new Date(b.crawler_dispatch_last_attempt_at || b.crawler_dispatch_failed_at || b.created_at).getTime() - new Date(a.crawler_dispatch_last_attempt_at || a.crawler_dispatch_failed_at || a.created_at).getTime());
}
function latestWeekendProgress(rows: WeekendBonusProgressRow[]) {
  return [...rows].sort((a, b) => new Date(b.weekend_start_at || b.updated_at).getTime() - new Date(a.weekend_start_at || a.updated_at).getTime())[0] ?? null;
}
function latestRegularRebateCandidate(rows: RegularRebateCandidateRow[]) {
  return [...rows].sort((a, b) => new Date(b.deposited_at).getTime() - new Date(a.deposited_at).getTime())[0] ?? null;
}
function postGraduationRewards(detail: MemberDetail, types: string[]) {
  return detail.rewards.filter((row) => types.includes(row.reward_type)).sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
}
function postGraduationPayoutFocus(detail: MemberDetail) {
  return postGraduationRewards(detail, [
    "regular_deposit_rebate",
    "graduate_launch_red_packet_draw",
    "advanced_launch_red_packet_draw",
    "weekly_bright_red_packet_draw",
    "weekend_bright_red_packet_draw",
    "weekend_advanced_red_packet_draw"
  ]);
}
function graduateWeekHasData(voucherText: string, rewards: RewardRow[], detail: MemberDetail) {
  return Boolean(voucherText || rewards.length || detail.voucher_transactions?.some((row) => row.activity_code === "graduate_week"));
}
function graduateWeekBadge(voucherText: string, rewards: RewardRow[]) {
  if (rewards.length) return "有開券派發";
  if (voucherText) return "有紅包券";
  return "無資料";
}
function graduateWeekNextStep(voucherText: string, rewards: RewardRow[]) {
  if (rewards.some((row) => row.crawler_dispatch_status === "failed")) return "畢業後首週有派發失敗，請至贈禮小幫手任務處理。";
  if (rewards.length) return "已有畢業後首週開券派發紀錄，請依贈禮任務狀態追蹤。";
  if (voucherText) return "會員已持有畢業後首週紅包券，等待玩家開券。";
  return "目前沒有畢業首週資料。";
}
function rewardListSummary(rows: RewardRow[]) {
  if (!rows.length) return "-";
  return rows.slice(0, 3).map((row) => `#${row.id} / ${money(row.amount)} / ${rewardStatusLabel(row.reward_status)}`).join("；");
}
function rewardGiftHelperSummary(rows: RewardRow[]) {
  if (!rows.length) return "-";
  return rows.slice(0, 3).map((row) => giftHelperSummary(row)).join("；");
}
function regularRebateNextStep(detail: MemberDetail, row?: RegularRebateCandidateRow | null, reward?: RewardRow | null) {
  if (!memberIsGraduated(detail)) return "會員尚未畢業，不能建立常態回饋。";
  if (reward) return giftHelperNextStep(reward);
  if (!row) return "目前沒有可顯示的畢業後存款回饋資料。";
  if (row.status === "eligible") return "此筆存款可建立常態回饋，請確認後操作。";
  if (row.status === "manual_review") return "此筆存款需要人工確認，請查看常態回饋列表。";
  if (row.status === "created") return "常態回饋已建立，請至派發中心或贈禮小幫手任務追蹤。";
  return "目前不符合自動建立條件，請查看常態回饋列表。";
}
function postGraduationNextStep(detail: MemberDetail, failedCount: number, notifiableCount: number) {
  if (!memberIsGraduated(detail)) return "請先完成新人七關，畢業後才會開啟常態回饋與畢業後任務。";
  if (failedCount) return `此會員有 ${failedCount} 筆畢業後派發需處理，請至贈禮小幫手派發任務查看。`;
  if (notifiableCount) return `此會員有 ${notifiableCount} 筆已派發完成獎勵，可通知會員。`;
  if (!postGraduationPayoutFocus(detail).length && !(detail.weekend_bonus_progress ?? []).length) return "目前會員已畢業，但尚未有畢業後任務或回饋紀錄。";
  return "畢業後狀態正常，請依下方卡片查看回饋、紅包券與任務進度。";
}
function postGraduationPayoutHint(rows: RewardRow[], failedCount: number, notifiableCount: number) {
  if (failedCount) return `有 ${failedCount} 筆派發失敗或逾時，請優先處理。`;
  if (notifiableCount) return `有 ${notifiableCount} 筆已派發完成，可至通知清單通知會員。`;
  if (!rows.length) return "目前沒有畢業後派發紀錄。";
  return "畢業後派發目前沒有明顯異常。";
}
function rewardStatusBadgeClass(status?: string | null) {
  const text = String(status ?? "");
  if (text === "completed" || text.includes("已完成") || text.includes("已派發") || text.includes("已畢業")) return "status-pill success";
  if (text === "ready" || text.includes("等待") || text.includes("派發中")) return "status-pill soon";
  if (text === "failed" || text === "rejected" || text.includes("失敗") || text.includes("拒絕") || text.includes("逾時")) return "status-pill danger";
  if (text === "canceled" || text === "cancelled" || text.includes("取消")) return "status-pill locked";
  return "status-pill muted";
}
function giftHelperTaskId(reward?: RewardRow | null) { return reward?.crawler_dispatch_task_id ? `#${reward.crawler_dispatch_task_id}` : "-"; }
function giftHelperAttemptText(reward?: RewardRow | null) {
  if (!reward?.crawler_dispatch_task_id) return "-";
  const count = Number(reward.crawler_dispatch_attempt_count ?? 0);
  return `${Number.isFinite(count) ? count : 0} 次`;
}
function giftHelperFailedReason(reward?: RewardRow | null) {
  const text = String(reward?.crawler_dispatch_failed_reason ?? "").trim();
  return text || "-";
}
function giftHelperSummary(reward?: RewardRow | null) {
  if (!reward) return "-";
  if (!reward.crawler_dispatch_task_id) return "尚未排入贈禮小幫手";
  return `任務 #${reward.crawler_dispatch_task_id}｜${giftHelperTaskStatus(reward)}`;
}
function giftHelperTaskStatus(reward?: RewardRow | null) {
  if (!reward) return "-";
  if (!reward.crawler_dispatch_task_id) return "尚未排入贈禮小幫手";
  if (isGiftHelperProcessingTimedOut(reward)) return "處理逾時";
  const status = reward.crawler_dispatch_status ?? "pending";
  if (status === "pending") return giftHelperAvailabilityLabel(reward) === "尚未到派發時間" ? "尚未到派發時間" : "等待派發";
  return crawlerDispatchStatusLabel(status);
}
function giftHelperNextStep(reward?: RewardRow | null) {
  if (!reward) return "尚未建立派發紀錄。";
  if (!reward.crawler_dispatch_task_id) return "尚未排入贈禮小幫手。";
  if (isGiftHelperProcessingTimedOut(reward)) return "處理逾時，請至贈禮小幫手派發任務查看外部爬蟲狀態後處理。";
  const status = reward.crawler_dispatch_status ?? "pending";
  if (status === "pending") {
    if (giftHelperAvailabilityLabel(reward) === "尚未到派發時間") return "尚未到派發時間，系統不會提前交給贈禮小幫手。";
    return "等待贈禮小幫手派發。";
  }
  if (status === "processing") return "贈禮小幫手派發中，請等待回寫結果。";
  if (status === "completed") return "贈禮小幫手已派發完成。";
  if (status === "failed") return "贈禮小幫手派發失敗，請至贈禮小幫手派發任務處理。";
  if (status === "canceled") return "贈禮小幫手任務已取消，可至任務頁查看是否需重新排入。";
  return "請至派發中心或贈禮小幫手任務查看。";
}
function step7NextStepText(detail: MemberDetail) {
  const rewards = [detail.step7_final10_reward, detail.step7_graduation_reward].filter(Boolean) as RewardRow[];
  if (!rewards.length) return "等待第 7 關最後 10% 與畢業禮派發紀錄建立。";
  if (rewards.every((row) => row.reward_status === "completed")) return "第 7 關兩筆派發皆已完成。";
  const failed = rewards.find((row) => row.crawler_dispatch_status === "failed");
  if (failed) return `第 7 關有派發失敗：${giftHelperSummary(failed)}`;
  return rewards.map((row) => `${rewardTypeLabel(row.reward_type)}：${giftHelperSummary(row)}`).join("；");
}
function crawlerDispatchStatusLabel(status?: string | null) {
  if (status === "pending") return "等待派發";
  if (status === "processing") return "派發中";
  if (status === "completed") return "已派發完成";
  if (status === "failed") return "派發失敗";
  if (status === "canceled") return "已取消";
  return status || "-";
}
function giftHelperAvailabilityLabel(reward: Pick<RewardRow, "crawler_dispatch_status" | "crawler_dispatch_mode" | "crawler_dispatch_scheduled_at">) {
  if (reward.crawler_dispatch_status !== "pending") return "-";
  if (reward.crawler_dispatch_mode === "next_day_12_crawler_dispatch" && reward.crawler_dispatch_scheduled_at) {
    const scheduledAt = new Date(reward.crawler_dispatch_scheduled_at).getTime();
    if (Number.isFinite(scheduledAt) && scheduledAt > Date.now()) return "尚未到派發時間";
  }
  return "可派發";
}
function isGiftHelperProcessingTimedOut(reward: Pick<RewardRow, "crawler_dispatch_status" | "crawler_dispatch_last_attempt_at" | "crawler_dispatch_started_at">) {
  if (reward.crawler_dispatch_status !== "processing") return false;
  const basis = reward.crawler_dispatch_last_attempt_at || reward.crawler_dispatch_started_at;
  if (!basis) return false;
  const time = new Date(basis).getTime();
  return Number.isFinite(time) && time <= Date.now() - 30 * 60 * 1000;
}
function regularRebateStatusText(row: RegularRebateCandidateRow) {
  if (row.status === "created") {
    if (row.existing_reward_status === "completed") return "已派發";
    if (row.existing_reward_status === "ready") return "已建立 / 等待派發";
    if (row.existing_reward_status) return `已建立 / ${rewardStatusLabel(row.existing_reward_status)}`;
    return "已建立";
  }
  return row.status_label;
}
function inviteRelationStatusLabel(status?: string | null) {
  if (status === "active") return "已綁定";
  if (status === "completed") return "已完成";
  if (status === "pending") return "待確認";
  if (status === "rejected") return "已拒絕";
  if (status === "canceled") return "已取消";
  return status ?? "-";
}
function yesNoText(label: string, value?: number | null) { return `${label}：${value ? "是" : "否"}`; }
function amountOrDash(value?: number | null) { return Number(value ?? 0) > 0 ? money(Number(value)) : "未填"; }
function depositAmountText(value?: number | null, rewardId?: number | null) { return Number(value ?? 0) > 0 ? money(Number(value)) : rewardId ? "舊資料未記錄" : "未填"; }
function rewardStatusRef(id?: number | null, status?: string | null) {
  if (!id) return "尚未建立";
  if (status === "completed") return `#${id} 已派發`;
  if (status === "ready") return `#${id} 待派發`;
  return `#${id}｜${rewardStatusLabel(status)}`;
}
function depositSourceLabel(row: DepositRow) {
  const note = (row.note ?? "").toLowerCase();
  if (note.includes("crawler") || note.includes("import")) return "爬蟲 / 匯入";
  if (row.is_test) return "local/dev 測試";
  return "手動 / 系統";
}
function formatDepositIdList(value?: string | null) { try { const ids = JSON.parse(value ?? "[]"); return Array.isArray(ids) && ids.length ? ids.map((id) => `#${id}`).join("、") : "-"; } catch { return value || "-"; } }
function getMainProgress(detail: MemberDetail) { if (detail.member.status === "graduated" || detail.newbie_progress?.step7_status === "completed") return "第 7 關完成 / 新人畢業"; if (detail.step7_final10_reward || detail.step7_graduation_reward || detail.newbie_progress?.step7_status === "ready") return "第 7 關畢業派發中"; if (detail.step6_reward?.reward_status === "completed" || detail.newbie_progress?.step6_status === "completed") return "第 6 關完成 / 第 7 關可進行"; if (detail.step6_reward?.reward_status === "ready" || detail.newbie_progress?.step6_status === "ready") return "第 6 關等待派發"; if (detail.step5_reward?.reward_status === "completed" || detail.newbie_progress?.step5_status === "completed") return "第 5 關完成 / 第 6 關可進行"; if (detail.step5_reward?.reward_status === "ready" || detail.newbie_progress?.step5_status === "ready") return "第 5 關等待派發"; if (detail.step4_reward?.reward_status === "completed" || detail.newbie_progress?.step4_status === "completed") return "第 4 關完成 / 第 5 關可進行"; if (detail.step4_reward?.reward_status === "ready" || detail.newbie_progress?.step4_status === "ready") return "第 4 關等待派發"; if (detail.step3_reward?.reward_status === "completed" || detail.newbie_progress?.step3_status === "completed") return "第 3 關完成 / 第 4 關可進行"; if (detail.step2_reward?.reward_status === "completed" || detail.newbie_progress?.step2_status === "completed") return "第 2 關完成 / 第 3 關可進行"; if (detail.step1_reward?.reward_status === "completed" || detail.newbie_progress?.step1_status === "completed") return "第 1 關完成 / 第 2 關可進行"; if (detail.step1_reward?.reward_status === "ready") return "第 1 關等待派發"; return detail.member.status_label; }
function assetTypeLabel(type: string) { if (type === "gem") return "寶石碎片"; if (type === "glory" || type === "glory_value") return "閃耀值"; return type; }
function reasonLabel(reason: string) { if (reason === "step3_second_deposit") return "新人第 3 關二存加碼"; if (reason === "step5_third_deposit") return "新人第 5 關三存加碼"; if (reason === "step6_red_packet_completed") return "新人第 6 關紅包完成"; if (reason === "step6_red_packet_draw") return "新人第 6 關紅包抽獎"; if (reason === "gem_exchange_newbie_bonus") return "寶石兌換新手加碼紅包券"; if (reason === "graduate_week_checkin") return "畢業首週打卡"; if (reason.startsWith("redeem_")) return "寶石兌換"; return reason; }
function voucherTypeLabel(type: string) { if (type === "newbie_bonus_red_packet") return "新手加碼紅包券"; if (type === "starter_bonus") return "新手加碼紅包券（legacy）"; if (type === "newbie_launch_red_packet") return "新人啟航紅包券"; if (type === "graduate_launch_red_packet") return "畢業啟航紅包券"; if (type === "advanced_launch_red_packet") return "高級啟航紅包券"; if (type === "weekly_bright_red_packet") return "週耀紅包券"; if (type === "weekend_bright_red_packet") return "週末耀旺紅包券"; if (type === "weekend_advanced_red_packet") return "高級週末紅包券"; if (type === "weekly_glory") return "週耀紅包券（legacy）"; return type; }
function voucherActionLabel(action: string) { if (action === "issue") return "發放"; if (action === "use") return "使用"; if (action === "exchange") return "兌換"; return action; }
function voucherStatusLabel(status: string) { if (status === "available") return "可用"; if (status === "used") return "已使用"; if (status === "expired") return "已過期"; return status; }
function drawStatusLabel(status: string) { if (status === "drawn") return "已抽獎"; if (status === "completed") return "已完成"; if (status === "canceled") return "已取消"; return status; }
function voucherNoteLabel(note?: string | null) { if (note === "issue voucher") return "發放紅包券"; if (note === "red packet draw") return "紅包抽獎"; if (note === "use newbie launch red packet") return "使用新人啟航紅包券"; return note ?? "-"; }
function sourceLabel(source?: string | null) { if (!source) return "-"; if (source === "gems") return "寶石兌換"; if (source === "newbie_step5_completed") return "新人第 5 關完成"; if (source === "newbie_step6_red_packet") return "新人第 6 關紅包"; if (source === "newbie") return "新人七關"; if (source === "red-packet") return "紅包抽獎"; if (source === "graduate_week") return "畢業首週"; if (source === "weekend-bonus") return "週末耀旺"; return source; }
